import axios from 'axios';
import Vue from 'vue';
import to from 'await-to-js';
import moment from 'moment';
import {
  find,
  isArray,
  uniqueId,
  get,
  forEach,
  has,
  isEmpty,
  isNumber,
  merge,
  map,
  uniqBy,
  keys,
  cloneDeep,
  values,
  isObject,
  range,
  union,
} from 'lodash';
import DataTypes from '@enums/data-types';
import FilterOps from '@enums/filter-operations';
import { payloadKeySeparator } from '@enums/notifications';
import downloadXlsxFile from '../utils/download-xlsx-file';
import { formatFilters } from '../utils/filters';
import { computeScenarioPrice } from '../utils/price-override';
import sortStoreGroups from '../../utils/sort-store-groups-util';
import agGridUtils from '../../utils/ag-grid-utils';
import {
  getAllowedToolStoreGroupKeys,
  buildLayoutHideLookup,
  getCompetitorColumnHide,
  processColumnForToolStoreGroupFilter,
} from '../../utils/grid-view-column-state-utils';
import { isSameOrAfter } from '@sharedModules/data/utils/dates-utils';
import {
  mapAggregations,
  getTimeFlexibleItems,
} from '@sharedModules/data/utils/time-flexible-utils';
import { updateProductWithUnsavedKpiResults } from '@sharedModules/helpers/price-overrides.helpers';
import timeFlexibleEndings from '@enums/time-flexible-endings';
import { yearly } from '@enums/historical-periods';
import { yearMonthDayFormat } from '@enums/date-formats';
import { jobStatuses } from '@enums/jobapi';
import { isAuditLogFlagTrue } from '@/js/utils/audit-log';
import {
  rootHierarchyLevel,
  unitLevel,
  categoryLevel,
  architectureGroupLevel,
  pricingGroupLevel,
} from '@enums/hierarchy';
import {
  competitorIndexWeightingTypes,
  displayRulesListView,
  displayHistoricalDropdown,
  getGroupedAggregations,
  getSingleAggregations,
  saveResultsToDatabricks,
  showUnsimulatedIndex,
  useAgGridProductList,
  useStickersPromotion,
  useStoreGroupChains,
  enablePriceOptimisation,
  enableVolumeForecast,
} from '@enums/feature-flags';
import { categoryManagerPermissions, blacklistedAlerts } from '@enums/alert-permissions';
import filterPredicates from '@enums/filter-predicates';
import { paramsSerializer } from '../../utils/legacy-params-serializer';
import filterData from '../../utils/filter-data-util';
import gridTypes from '@enums/grid-types';
import filterOptions from '@enums/filter-options';
import { getFilterValue } from '../../utils/filter-util';
import Events from '@enums/events';
import { descending } from '@enums/sort-direction';

const allHierarchyLevels = [
  rootHierarchyLevel,
  unitLevel,
  categoryLevel,
  pricingGroupLevel,
  architectureGroupLevel,
];

const makeHierarchyKey = (aggregationLevel, parentId, competitors) =>
  `${aggregationLevel}::${parentId}::${competitors.join('::')}`;

const makeGroupedHierarchyKey = competitors => `${competitors.join('::')}`;

const defaultExpandedHierarchyLevelItems = rootHierarchyLevelItemId => [
  rootHierarchyLevelItemId,
  rootHierarchyLevelItemId,
  null,
  null,
  null,
];

const triggerStatusNotification = ({ context, jobId, jobStatus, notificationPayload } = {}) => {
  const { commit } = context;

  console.log('statusToSend', {
    id: `${jobId}:${jobStatus}:${new Date().toISOString()}`,
    jobStatus,
    notificationPayload,
    baseTranslation: `notifications.${jobId}`,
  });
  commit(
    'notifications/addNotification',
    {
      id: `${jobId}:${jobStatus}:${new Date().toISOString()}`,
      jobStatus,
      notificationPayload,
      baseTranslation: `notifications.${jobId}`,
    },
    { root: true }
  );
};

const approxEq = (v1, v2, epsilon) => {
  if (epsilon == null) {
    epsilon = 0.001;
  }
  return Math.abs(v1 - v2) < epsilon;
};
const objectDifferenceLogger = (objectA, objectB, keysToIgnore = []) => {
  const objectDifferences = Object.keys(objectA).reduce((diff, key) => {
    if (
      isObject(objectA[key]) ||
      objectA[key] === objectB[key] ||
      approxEq(objectA[key], objectB[key]) ||
      keysToIgnore.includes(key)
    )
      return diff;
    if (!has(objectB, key)) {
      diff += `\n${key} Old: ${objectA[key]} Grouped: 'missing'`;
      return diff;
    }
    diff += `\n${key} Old: ${objectA[key]} Grouped: ${objectB[key]} Delta: ${Math.abs(
      objectA[key] - objectB[key]
    )}`;
    return diff;
  }, '');
  if (!isEmpty(objectDifferences)) console.error(objectDifferences);
  else console.log('No difference');
};

const getFilteredAlerts = (alerts, isPricingSpecialist) => {
  if (isEmpty(alerts)) {
    return [];
  }
  const nonBlacklistedAlerts = alerts.filter(alert => !blacklistedAlerts.has(alert.alertType));
  if (isPricingSpecialist) return nonBlacklistedAlerts;
  return nonBlacklistedAlerts.filter(alert => categoryManagerPermissions.includes(alert.alertType));
};

let activeSource = null;
let activeRequest = null;

const fetchItemsWithLimit = async (
  { state, rootState, rootGetters, commit, getters },
  { isUnitManagerView, levelEntryKey, formattedAttributeFilters },
  limit = 0
) => {
  const scenarioKey = rootState.filters.scenario;
  const workpackageId = rootState.workpackages.selectedWorkpackage._id;

  const baseUrl = `/api/workpackage-product/workpackage/${workpackageId}/gridview/${levelEntryKey}`;
  const url = isUnitManagerView ? `${baseUrl}/unit` : `${baseUrl}/scenario/${scenarioKey}`;

  // Extract pick fields from current ag-grid column state if available
  let pickFields = null;
  if (getters.layoutActivated && state.currentColumnState) {
    pickFields = agGridUtils.extractPickFieldsFromColumnState(
      state.currentColumnState,
      agGridUtils.fieldDependencies,
      state.selectedHistoricalPeriod,
      rootState.clientConfig.releaseFlags
    );
  }

  let displayedAttributes = [];
  if (getters.isAgGridProductListEnabled) {
    const getGridDisplayedAttributes = rootGetters['attributes/getGridDisplayedAttributes'];
    displayedAttributes = Array.from(getGridDisplayedAttributes);
  }

  // Merge attributes from pickFields with existing displayedAttributes
  // temp solution, we need to remove displayedAttributes as pickFields give us full set
  // https://owlabs.atlassian.net/browse/PRICE-4881
  displayedAttributes = agGridUtils.mergeAttributesFromPickFields(displayedAttributes, pickFields);

  const useStoreGroupChainsFlag = rootState.clientConfig.toggleLogic[useStoreGroupChains];

  const competitors = useStoreGroupChainsFlag
    ? getters.competitors
    : union(Array.from(state.visibleCompetitors), getters.competitors);

  // Merge toolStoreGroupCompetitors from pickFields with existing ones
  // temp solution, we need to remove toolStoreGroupCompetitors as pickFields give us all info
  // https://owlabs.atlassian.net/browse/PRICE-4881
  const mergedToolStoreGroupCompetitors = useStoreGroupChainsFlag
    ? agGridUtils.mergeToolStoreGroupCompetitors(Array.from(state.visibleCompetitors), pickFields)
    : [];

  const params = {
    attributeFilters: formattedAttributeFilters,
    limit,
    competitorNames: getters.selectedCompetitorNames, // mongo-specific
    competitors, // databricks-specific
    selectedHistoricalPeriod: state.selectedHistoricalPeriod,
    displayedAttributes,
    toolStoreGroupCompetitors: mergedToolStoreGroupCompetitors,
    ...(pickFields ? { pick: pickFields } : {}),
  };

  // TODO: Remove this `if` as part of readFromDatabricks cleanup — https://owlabs.atlassian.net/browse/PRICE-4121
  // This block is no longer used and doesn't work, since getGridviewForUnit is now a POST request.
  // Previously used to stream the response in Unit Manager view due to the large number of products.
  if (isUnitManagerView && !getters.readFromDatabricks) {
    return new Promise((resolve, reject) => {
      if (activeSource) {
        activeSource.close();
      }

      // legacy params serializer from axios 0.2x.
      const fullUrl = `${url}?${paramsSerializer.serialize(params)}`;

      const source = new EventSource(fullUrl, { withCredentials: true });

      let isFirstChunk = true;
      source.addEventListener('message', function(e) {
        const items = JSON.parse(e.data);
        // this can be useful as the data size is too large to be inspected in chrome dev tools
        console.log(items.length, ' items received for Grid View');

        const isPricingSpecialist = rootGetters['context/isPricingSpecialist'];
        items.forEach(item => {
          item.filteredAlerts = getFilteredAlerts(item.alerts, isPricingSpecialist);
          // filtering by alert type performs by alertCounts field, hence all alerts are not required anymore
          delete item.alerts;
        });

        if (isFirstChunk) {
          commit('setItems', { items });
          isFirstChunk = false;
        } else {
          commit('appendItems', { items });
        }
      });

      source.addEventListener('error', function(e) {
        console.log('Closing Grid View EventSource');
        if (e.eventPhase === EventSource.CLOSED) {
          source.close();
          activeSource = null;
          resolve(); // Resolve the promise when the stream is done
        } else {
          reject(new Error('EventSource failed')); // Reject if there's an error
        }
      });

      source.addEventListener('done', function() {
        source.close();
        activeSource = null;
        resolve(); // Resolve when all data is received
      });

      activeSource = source;
    });
  }

  // Cancel previous Axios request if still active
  if (activeRequest && typeof activeRequest.cancel === 'function') {
    activeRequest.cancel('Operation canceled due to new request.');
  }
  const axiosSource = axios.CancelToken.source();
  activeRequest = axiosSource;

  const [err, response] = await to(axios.post(url, params, { cancelToken: axiosSource.token }));

  // Only clear activeRequest if it is still the current one
  if (activeRequest === axiosSource) {
    activeRequest = null;
  }

  if (err) {
    if (axios.isCancel && axios.isCancel(err)) {
      // Request was canceled, do not throw error
      return;
    }
    throw new Error(err.message);
  }

  const { data: items } = response;

  const isPricingSpecialist = rootGetters['context/isPricingSpecialist'];
  items.forEach(item => {
    item.filteredAlerts = getFilteredAlerts(item.alerts, isPricingSpecialist);
    // filtering by alert type performs by alertCounts field, hence all alerts are not required anymore
    delete item.alerts;
  });

  commit('setItems', { items });
};

const mapStoreGroupAggregationsData = ({ data, products, storeGroupOrder }) => {
  if (isEmpty(data)) {
    // Create default aggregations -> all we need is the _id field for display
    // all other fields will default to - or 0 due to the number formatter
    const toolStoreGroupsInPG = uniqBy(
      map(products, item => ({ _id: item.toolStoreGroupDescription })),
      '_id'
    );
    return sortStoreGroups(toolStoreGroupsInPG, storeGroupOrder, '_id');
  }
  return sortStoreGroups(data, storeGroupOrder, '_id');
};

const areStoreGroupAggregationsEqual = ({
  level,
  parentId,
  scenarioKey,
}) => storeGroupAggregations =>
  storeGroupAggregations.parentId === parentId &&
  storeGroupAggregations.level === level &&
  storeGroupAggregations.scenarioKey === scenarioKey;

const getFetchStoreGroupAggregationsParams = ({ level, parentId, scenarioKey }) => {
  const dynamicParams = scenarioKey
    ? {
        pricingGroupId: parentId,
        scenarioKey,
      }
    : {
        aggregationLevel: level + 1,
        parentId,
      };
  return {
    aggregateStoreGroups: true,
    ...dynamicParams,
  };
};

const volumeKpiInputFields = item => {
  return keys(timeFlexibleEndings).reduce((acc, ending) => {
    acc[`volume_${timeFlexibleEndings[ending]}`] = get(item, `volume${ending}`, 0);
    return acc;
  }, {});
};

// For Databricks, we support table filters by collecting all productKeys from the filtered items
function addActiveTableFilters(state, getters) {
  const filters = [...state.activeTableFilters];

  if (!getters.readFromDatabricks || isEmpty(state.activeTableFilters)) {
    return filters;
  }

  filters.push({
    datatype: DataTypes.str,
    op: FilterOps.isOneOf,
    value: getters.filteredItems.map(item => item.productKey),
    path: 'productKey',
    id: 'productKeyDatabricksFilter',
  });

  // Needed when the user narrows down toolStoreGroups in the products table compared to a broader filter at the top level
  if (state.activeTableFilters.some(filter => filter.path === 'toolStoreGroupDescription')) {
    filters.push({
      datatype: DataTypes.str,
      op: FilterOps.isOneOf,
      // keep only unique toolStoreGroupKeys
      value: Array.from(new Set(getters.filteredItems.map(item => item.toolStoreGroupKey))),
      path: 'toolStoreGroupKey',
      id: 'toolStoreGroupKeyDatabricksFilter',
    });
  }

  return filters;
}

const getExtraFetchHierarchyItemAggregationParams = (
  state,
  getters,
  {
    aggregationLevel,
    parentId,
    aggregateScenarios,
    aggregateStoreGroups,
    pricingGroupId,
    scenarioKey,
    historicalPeriod,
  } = {}
) => {
  if (aggregateScenarios) {
    return {
      aggregateScenarios,
      pricingGroupId,
      activeTableFilters: addActiveTableFilters(state, getters),
      historicalPeriod,
    };
  }
  if (aggregateStoreGroups && scenarioKey) {
    return {
      aggregateStoreGroups,
      scenarioKey,
      pricingGroupId,
      activeTableFilters: addActiveTableFilters(state, getters),
      historicalPeriod,
    };
  }
  const basicParams = {
    parentId: parentId || state.rootHierarchyLevelItemId,
    parentLevel: Math.max(0, aggregationLevel - 1),
    aggregationLevel,
    historicalPeriod,
  };
  if (aggregateStoreGroups) {
    basicParams.aggregateStoreGroups = true;
  }
  return basicParams;
};

const getHierarchyLevelAggregationRequestKey = ({ level, aggregateStoreGroups }) =>
  aggregateStoreGroups ? `${level}-store-group` : level;

const defaultHierarchyLevelAggregationLoadings = () => {
  const loadings = {};
  allHierarchyLevels.forEach(level => {
    // we always load aggregations for the root level
    loadings[getHierarchyLevelAggregationRequestKey({ level, aggregateStoreGroups: false })] =
      level === rootHierarchyLevel;
    loadings[getHierarchyLevelAggregationRequestKey({ level, aggregateStoreGroups: true })] = false;
  });
  return loadings;
};

const unitAggregationsLoadingKey = getHierarchyLevelAggregationRequestKey({
  level: unitLevel,
});

const getInitialState = () => {
  return {
    rootHierarchyLevelItemId: '',
    hierarchy: {},
    groupedHierarchy: {},
    children: {},
    items: [],
    expandedHierarchyLevelItems: [],
    calculatingScenarioResults: false,
    calculatingTsgStrategicKeys: [],
    calculatingTsgElasticityKeys: [],
    calculatingPgResults: {},
    calculatingSpecificPgResults: {},
    calculatingScenarioElasticityResults: false,
    recalculatingAll: false,
    refreshingAlerts: {},
    loadingRecalculateAllResults: false,
    selectedCompetitors: [],
    previousSelectedCompetitors: [],
    loading: false,
    loadingItems: false,
    savingKpiResults: false,
    loadingMsg: null,
    loadingHierarchyView: false,
    aggregationLoadings: defaultHierarchyLevelAggregationLoadings(),
    timeFlexibleAggregationsLoadings: defaultHierarchyLevelAggregationLoadings(),
    timeFlexibleAggregationRequestsCanceller: {},
    pricingGroupsToRelease: [],
    categoryToRelease: null,
    activeTableFilters: [],
    previousActiveTableFilters: [],
    selectedWeighting: competitorIndexWeightingTypes.sales, // set from feature flags fetched from client config store
    showRegularImpact: false,
    selectedHistoricalPeriod: yearly,
    failedRuns: [],
    expandedProduct: null,
    alertFilter: null,
    alertCounts: {},
    loadingProductSettings: false,
    loadingStoreGroupAggregations: false,
    expandedStoreGroupAggregations: [],
    expandedProductSettings: {},
    isFilteringByArchitectureGroupEnabled: false,
    downloadingItems: false,
    isCachedResult: false,
    cachedResultTimestamp: null,
    showAgGridProductList: true,
    hasTemporaryUnsavedKpiResults: false,
    canEngineRunResults: { canEngineRun: true, missingProduct: null, invalidProduct: null },
    visibleCompetitors: new Set(), // If useStoreGroupChains: Set of "tsgKey::competitorKey"; otherwise: Set of "competitorKey"s only
    currentColumnState: null, // Store current ag-grid column state for pick parameter
    // Bulk selection derived state (metadata for cross-component coordination)
    // Primary selection data (selectedRowsByTsg) lives in bulkPriceOverride mixin
    // This state contains only derived summary for UI controls (engine-controls, etc.)
    bulkSelectionState: {
      selectedCount: 0, // Total selected products (visible TSGs only)
      tsgKey: null, // Single TSG key (null if multi-TSG or none selected)
      hasLock: false, // Whether single selected TSG is locked
      multiTsg: false, // Whether selections span multiple TSGs
      isLoading: false, // Whether bulk operation is in progress
    },
    // Last triggered engine run information keyed by scenario/TSG key
    lastTriggeredInfo: {
      // Last strategic engine run information. All runs (including skip_optimisation true etc).
      strategic_engine: {},
      // Last full (non-skip_optimisation) strategic engine run information
      strategic_engine_full_run: {},
      cross_elasticity_engine: {},
    },
    isLastTriggeredLoading: {
      strategic_engine: false,
      cross_elasticity_engine: false,
    },
    // History sections for workpackage settings modal
    dataRefreshHistory: [],
    engineRunHistory: [],
  };
};

const store = {
  namespaced: true,

  state: getInitialState(),

  getters: {
    items: state => state.items,

    filteredItems: state => {
      const timeFlexibleItems = getTimeFlexibleItems(state.items, state.selectedHistoricalPeriod);
      const result = filterData(timeFlexibleItems, state.activeTableFilters, state.alertFilter);
      return result;
    },

    competitors: (state, getters) => {
      return getters.readFromDatabricks
        ? getters.selectedCompetitorKeys
        : getters.selectedCompetitorNames;
    },

    previousCompetitors: (state, getters) => {
      return getters.readFromDatabricks
        ? getters.previousSelectedCompetitorKeys
        : getters.previousSelectedCompetitorNames;
    },

    hierarchyLevelItems: (state, getters, rootState) => ({ level, parentId } = {}) => {
      const getSingleAggregationsFlag = rootState.clientConfig.toggleLogic[getSingleAggregations];

      if (getSingleAggregationsFlag) {
        const hierarchyKey = makeHierarchyKey(level, parentId, getters.competitors);
        return state.hierarchy[hierarchyKey] || [];
      }
      const hierarchyKey = makeGroupedHierarchyKey(getters.competitors);
      return (
        values(state.hierarchy[hierarchyKey][level]).filter(agg => agg.parentId === parentId) || []
      );
    },

    // for generating a matching hierarchy key in hierarchy.vue
    hierarchyKey: (state, getters, rootState) => ({ level, parentId } = {}) => {
      const getSingleAggregationsFlag = rootState.clientConfig.toggleLogic[getSingleAggregations];
      if (getSingleAggregationsFlag) {
        return makeHierarchyKey(level, parentId, getters.competitors);
      }
      return makeGroupedHierarchyKey(getters.competitors);
    },

    groupedHierarchyKey: (state, getters) => () => {
      return makeGroupedHierarchyKey(getters.competitors);
    },

    previousGroupedHierarchyKey: (state, getters) => () => {
      return makeGroupedHierarchyKey(getters.previousCompetitors);
    },

    selectedCompetitorNames: state =>
      state.selectedCompetitors.map(competitor => competitor.competitorDescription),

    selectedCompetitorKeys: state =>
      state.selectedCompetitors.map(competitor => competitor.competitorKey),

    previousSelectedCompetitorNames: state =>
      state.previousSelectedCompetitors.map(competitor => competitor.competitorDescription),

    previousSelectedCompetitorKeys: state =>
      state.previousSelectedCompetitors.map(competitor => competitor.competitorKey),

    selectedCompetitorDisplayDescriptions: state =>
      state.selectedCompetitors.map(competitor => competitor.competitorDisplayDescription),

    expandedStoreGroupAggregation: (state, getters) => ({ level, parentId, scenarioKey } = {}) =>
      getters.storeGroupAggregationsBySelectedHistoricalPeriod.find(
        areStoreGroupAggregationsEqual({ level, parentId, scenarioKey })
      ),

    storeGroupAggregationsBySelectedHistoricalPeriod: (
      { expandedStoreGroupAggregations, selectedHistoricalPeriod }, // state
      { readFromDatabricks }, // getters
      { clientConfig } // rootState
    ) => {
      const mapAggregationsBySelectedHistoricalPeriod = mapAggregations(selectedHistoricalPeriod);
      const storeGroupOrder = clientConfig.storeGroupOrderConfig;
      return expandedStoreGroupAggregations.map(aggregation => {
        return {
          ...aggregation,
          data: sortStoreGroups(
            aggregation.data.map(item => ({
              _id: item._id,
              toolStoreGroupKey: item.toolStoreGroupKey,
              ...(readFromDatabricks ? item : mapAggregationsBySelectedHistoricalPeriod(item)),
            })),
            storeGroupOrder,
            'toolStoreGroupDescription'
          ),
        };
      });
    },

    timeFlexibleAggregationsLoading: state =>
      Object.values(state.timeFlexibleAggregationsLoadings).some(Boolean),

    aggregationsLoading: state =>
      // aggregations are slow, checking the top two levels should be sufficient
      !!state.aggregationLoadings[unitAggregationsLoadingKey],

    hasUnfinishedSpecificPgJobs(state) {
      return Object.values(state.calculatingSpecificPgResults).some(isCalculating => isCalculating);
    },

    readFromDatabricks(state, getters, rootState) {
      return rootState.clientConfig.toggleLogic[saveResultsToDatabricks];
    },

    useNewPriceOverridesAPI(state, getters, rootState) {
      return get(rootState, 'clientConfig.releaseFlags.enableNewPriceOverrideAPI', false);
    },

    unsavedKpiResults: state => state.items.filter(item => item.hasUnsavedKpiResults),

    // for current displayed items in gridview PG / unit for impact view
    hasUnsavedKpiResults: (state, getters) =>
      getters.readFromDatabricks && !isEmpty(getters.unsavedKpiResults),

    hasUnsavedKpiResultsForWorkpackage(state, getters) {
      const groupedHierarchyKey = makeGroupedHierarchyKey(getters.competitors);
      const item = get(
        state.groupedHierarchy,
        `${groupedHierarchyKey}.${rootHierarchyLevel}.${state.rootHierarchyLevelItemId}`
      );
      // Return true if either temporary flag is set or hierarchy indicates unsaved changes
      return state.hasTemporaryUnsavedKpiResults || (item ? item.hasUnsavedKpiResults : false);
    },

    getColspan: (state, getters, rootState) => colspan => {
      const selectedCompetitorsCount = isEmpty(state.selectedCompetitors)
        ? rootState.clientConfig.toggleLogic.defaultCompetitorsDisplayCount
        : state.selectedCompetitors.length;
      const competitorColumnsPerGroup = rootState.clientConfig.toggleLogic[showUnsimulatedIndex]
        ? 6
        : 4;
      return selectedCompetitorsCount * competitorColumnsPerGroup + colspan;
    },

    // Check if any of the loading states are true
    // We need to track multiple loading states to ensure the grid view accurately reflects the current loading status.
    // This includes general loading, hierarchy view loading, aggregations loading, and recalculating all results.
    isGridViewLoading: state => {
      return (
        state.loading ||
        state.loadingHierarchyView ||
        state.aggregationsLoading ||
        (state.loadingRecalculateAllResults && !state.recalculatingAll)
      );
    },
    isAgGridProductListEnabled: (state, getters, rootState) => {
      return (
        rootState.clientConfig.toggleLogic[useAgGridProductList] && state.showAgGridProductList
      );
    },
    itemHasUpcomingPromotion: (state, getters, rootState) => item => {
      const hasUpcomingPromotion = item.hasUpcomingPromotion;
      const childPromotionEnabled = rootState.clientConfig.toggleLogic[useStickersPromotion];

      if (childPromotionEnabled) {
        const childUpcomingPromotions = get(item, 'childPromotions.upcomingPromotion');
        return hasUpcomingPromotion || !isEmpty(childUpcomingPromotions);
      }
      return hasUpcomingPromotion;
    },
    itemOnPromotion: (state, getters, rootState) => item => {
      const onPromotion = item.isOnPromotion;
      const childPromotionEnabled = rootState.clientConfig.toggleLogic[useStickersPromotion];

      if (childPromotionEnabled) {
        const childCurrentPromotions = get(item, 'childPromotions.currentPromotion');
        return onPromotion || !isEmpty(childCurrentPromotions);
      }
      return onPromotion;
    },
    isUnitManagerView: (state, getters, rootState) => {
      return rootState.route.name === 'pricingImpactView';
    },
    isOverrideToBeExpired: (state, getters, rootState) => (override = {}) => {
      if (!override.expiryDate) return false;

      const goLiveDate = rootState.workpackages.selectedWorkpackage.goLiveDate;
      const momentTodaysDate = moment();
      const momentGoLiveDate = moment(goLiveDate, yearMonthDayFormat, true);
      const momentExpiryDate = moment(override.expiryDate, yearMonthDayFormat, true);

      const expired = isSameOrAfter(momentTodaysDate, momentExpiryDate);
      const toBeExpired = !expired && isSameOrAfter(momentGoLiveDate, momentExpiryDate);

      return toBeExpired;
    },

    visibleCompetitors: state => state.visibleCompetitors,

    layoutActivated: (_, getters, rootState, rootGetters) => {
      return (
        getters.isAgGridProductListEnabled &&
        rootGetters['gridLayouts/getActiveLayout'](gridTypes.GRIDVIEW)
      );
    },

    isWorkpackageAlertsRefreshing: (state, getters, rootState) => {
      const workpackageId = get(rootState.workpackages.selectedWorkpackage, '_id');
      return get(state.refreshingAlerts, workpackageId, false);
    },
  },

  mutations: {
    setLoading(state, isLoading) {
      state.loadingMsg = null;
      state.loading = isLoading;
    },

    setLoadingItems(state, isLoading) {
      state.loadingMsg = null;
      state.loading = isLoading;
      state.loadingItems = isLoading;
    },

    setSavingKpiResults(state, isSaving) {
      state.savingKpiResults = isSaving;
    },

    setLoadingWithMsg(state, loadingMsg) {
      state.loadingMsg = loadingMsg;
      state.loading = true;
    },

    setLoadingHierarchyView(state, isLoading) {
      state.loadingHierarchyView = isLoading;
    },

    setDownloadingItems(state, downloading) {
      state.downloadingItems = downloading;
    },

    setTimeFlexibleAggregationsLoading(state, { level, aggregateStoreGroups, isLoading }) {
      const key = getHierarchyLevelAggregationRequestKey({ level, aggregateStoreGroups });
      state.timeFlexibleAggregationsLoadings = {
        ...state.timeFlexibleAggregationsLoadings,
        [key]: isLoading,
      };
    },

    resetTimeFlexibleAggregationsLoading(state) {
      state.timeFlexibleAggregationsLoadings = defaultHierarchyLevelAggregationLoadings();
    },

    setAggregationLoadings(state, { level, aggregateStoreGroups, isLoading }) {
      const key = getHierarchyLevelAggregationRequestKey({ level, aggregateStoreGroups });
      state.aggregationLoadings = {
        ...state.aggregationLoadings,
        [key]: isLoading,
      };
    },

    resetAggregationLoadings(state) {
      state.aggregationLoadings = defaultHierarchyLevelAggregationLoadings();
    },

    setTimeFlexibleAggregationRequestsCanceller(state, { level, aggregateStoreGroups, canceller }) {
      const key = getHierarchyLevelAggregationRequestKey({ level, aggregateStoreGroups });
      state.timeFlexibleAggregationRequestsCanceller[key] = canceller;
    },

    removeTimeFlexibleAggregationRequestsCanceller(state, { level, aggregateStoreGroups }) {
      const key = getHierarchyLevelAggregationRequestKey({ level, aggregateStoreGroups });
      delete state.timeFlexibleAggregationRequestsCanceller[key];
    },

    resetTimeFlexibleAggregationRequestsCanceller(state) {
      state.timeFlexibleAggregationRequestsCanceller = {};
    },

    setRootHierarchyLevelItemId(state, { rootHierarchyLevelItem }) {
      state.rootHierarchyLevelItemId = rootHierarchyLevelItem.levelEntryKey;
    },

    setItems(state, { items }) {
      state.items = items;
    },

    appendItems(state, { items }) {
      state.items.push(...items);
    },

    updateItem(state, { updateItem }) {
      if (!updateItem._id) {
        console.error('product should have _id for override to work:', updateItem);
      }
      const idx = state.items.findIndex(i => i._id === updateItem._id);
      if (idx !== -1) {
        const originalItem = state.items[idx];
        Vue.set(state.items, idx, merge(originalItem, updateItem));
      } else {
        console.warn('Item not found in state.items:', updateItem._id);
      }
    },

    setCategoryToRelease(state, { category }) {
      state.categoryToRelease = category;
    },

    setPricingGroupsToRelease(state, { pricingGroups }) {
      state.pricingGroupsToRelease = pricingGroups;
    },

    updatePricingGroupsToRelease(state, { pg, selected = true }) {
      // Keep the release list in sync with checkbox toggles: add on select, remove on deselect.
      const pgIndex = state.pricingGroupsToRelease.findIndex(
        pricingGroup => pricingGroup.levelEntryKey === pg.levelEntryKey
      );

      if (!selected) {
        if (pgIndex !== -1) {
          state.pricingGroupsToRelease.splice(pgIndex, 1);
        }
        return;
      }

      if (pgIndex === -1) {
        state.pricingGroupsToRelease.push(pg);
      }
    },

    resetPricingGroupsToRelease(state) {
      state.pricingGroupsToRelease = [];
    },

    setLastTriggeredInfo(state, { engineType, key, data }) {
      if (!state.lastTriggeredInfo[engineType]) {
        Vue.set(state.lastTriggeredInfo, engineType, {});
      }
      Vue.set(state.lastTriggeredInfo[engineType], key, data);
    },

    setLastTriggeredLoading(state, { engineType, isLoading }) {
      Vue.set(state.isLastTriggeredLoading, engineType, isLoading);
    },

    resetLastTriggeredInfo(state) {
      state.lastTriggeredInfo = {
        strategic_engine: {},
        cross_elasticity_engine: {},
        strategic_engine_full_run: {},
      };
    },

    setDataRefreshHistory(state, history) {
      state.dataRefreshHistory = history;
    },

    setEngineRunHistory(state, history) {
      state.engineRunHistory = history;
    },

    setHierarchyLevelItems(state, { items, aggregationLevel, parentId, selectedCompetitors }) {
      const hierarchyKey = makeHierarchyKey(aggregationLevel, parentId, selectedCompetitors);
      state.hierarchy = {
        ...state.hierarchy,
        [hierarchyKey]: items,
      };
    },

    setGroupedHierarchyLevelItems(state, { items, selectedCompetitors }) {
      const groupedHierarchyKey = makeGroupedHierarchyKey(selectedCompetitors);
      state.groupedHierarchy = {
        ...state.groupedHierarchy,
        [groupedHierarchyKey]: items,
      };
    },

    setTemporaryUnsavedKpiResults(state, temporaryUnsavedKpiResults) {
      state.hasTemporaryUnsavedKpiResults = temporaryUnsavedKpiResults;
    },

    // for fillhandle using bulk api, need to set unsavedKpiResults for all changed items.
    setBulkUnsavedKpiResults(state, { itemsWithUnsavedKpiResults }) {
      state.items = state.items.map(item => {
        const hasUnsavedKpiResults = itemsWithUnsavedKpiResults.some(
          unsavedItem => unsavedItem._id === item._id
        );
        if (hasUnsavedKpiResults) {
          item.hasUnsavedKpiResults = true;
        }
        return item;
      });
    },

    setHierarchyUnsavedKpiResults(state, { hierarchyKey, selectedCompetitors, revert = false }) {
      const groupedHierarchyKey = makeGroupedHierarchyKey(selectedCompetitors);
      let levelEntryKey = hierarchyKey;

      // set hasUnsavedKpiResults on each hierarchy level based on previous hierarchy's parentId
      for (let level = pricingGroupLevel; level >= rootHierarchyLevel; level -= 1) {
        state.groupedHierarchy[groupedHierarchyKey][level][
          levelEntryKey
        ].hasUnsavedKpiResults = !revert;

        const { parentId } = state.groupedHierarchy[groupedHierarchyKey][level][levelEntryKey];

        if (revert) {
          const siblings = Object.values(state.groupedHierarchy[groupedHierarchyKey][level]).filter(
            v => v.parentId === parentId
          );
          const siblingsHaveUnsavedKpiResults = siblings.some(v => v.hasUnsavedKpiResults);
          // only revert parent level when no siblings have unsaved kpi results either
          if (siblingsHaveUnsavedKpiResults) return;
        }

        levelEntryKey = parentId;
      }
    },

    initExpandedHierarchyLevelItems(state) {
      // set state.expandedHierarchyLevelItems to default if hierarchy wasn't expanded
      state.expandedHierarchyLevelItems = state.expandedHierarchyLevelItems.length
        ? state.expandedHierarchyLevelItems
        : defaultExpandedHierarchyLevelItems(state.rootHierarchyLevelItemId);
    },

    setExpandedHierarchyLevelItems(state, expandedHierarchyLevelItems) {
      state.expandedHierarchyLevelItems = expandedHierarchyLevelItems;
    },

    setFailedRuns(state, failedRuns) {
      state.failedRuns = failedRuns;
    },

    resetExpandedHierarchyLevels(state) {
      state.expandedHierarchyLevelItems = [];
    },

    saveAndResetGridViewFilters(state) {
      state.previousActiveTableFilters = cloneDeep(state.activeTableFilters);
      state.activeTableFilters = [];
      state.isFilteringByArchitectureGroupEnabled = true;
    },

    restoreGridViewFilters(state) {
      state.activeTableFilters = state.previousActiveTableFilters;
      state.previousActiveTableFilters = [];
      state.isFilteringByArchitectureGroupEnabled = false;
    },

    setCalculatingScenarioResults(state, isCalculating) {
      state.calculatingScenarioResults = isCalculating;
    },

    setCalculatingTsgStrategicKeys(state, tsgKeys) {
      state.calculatingTsgStrategicKeys = tsgKeys || [];
    },

    setCalculatingTsgElasticityKeys(state, tsgKeys) {
      state.calculatingTsgElasticityKeys = tsgKeys || [];
    },

    setCalculatingScenarioElasticityResults(state, isCalculating) {
      state.calculatingScenarioElasticityResults = isCalculating;
    },

    setCalculatingPgResults(state, { workpackageId, levelEntryKey, isCalculating }) {
      const index = `${workpackageId}${payloadKeySeparator}${levelEntryKey}`;
      Vue.set(state.calculatingPgResults, index, isCalculating);
    },
    setCalculatingSpecificPgResults(state, { workpackageId, pgLevelEntryKeys, isCalculating }) {
      // if no pgLevelEntryKeys set all existing keys to false
      if (isEmpty(pgLevelEntryKeys)) {
        // loop through all existing keys and set to false
        const existingKeys = keys(state.calculatingSpecificPgResults);
        existingKeys.forEach(existingKey => {
          Vue.set(state.calculatingSpecificPgResults, existingKey, false);
        });
        return;
      }

      pgLevelEntryKeys.forEach(pgLevelEntryKey => {
        const index = `${workpackageId}${payloadKeySeparator}${pgLevelEntryKey}`;
        Vue.set(state.calculatingSpecificPgResults, index, isCalculating);
      });
    },

    setCanEngineRunResults(state, canEngineRunResults) {
      state.canEngineRunResults = canEngineRunResults;
    },

    setSelectedCompetitors(state, selectedCompetitors) {
      state.previousSelectedCompetitors = [...state.selectedCompetitors];
      // setting initial default competitor selection: aggregation triggered separately
      state.selectedCompetitors = selectedCompetitors;
    },

    setSelectedCompetitorAtIndex(state, { selectedCompetitor, index }) {
      state.previousSelectedCompetitors = [...state.selectedCompetitors];
      Vue.set(state.selectedCompetitors, index, selectedCompetitor);
    },

    setSelectedWeighting(state, { selectedWeighting }) {
      state.selectedWeighting = selectedWeighting;
    },

    setShowRegularImpact(state, { showRegularImpact }) {
      state.showRegularImpact = showRegularImpact;
    },

    setSelectedHistoricalPeriod(state, { selectedHistoricalPeriod }) {
      state.selectedHistoricalPeriod = selectedHistoricalPeriod;
    },

    /**
     * Updates bulk selection metadata with shallow equality check.
     *
     * This mutation is called frequently (on every checkbox click) from the bulkPriceOverride mixin.
     * The shallow equality check prevents unnecessary Vue reactivity updates when values haven't
     * actually changed, improving performance and preventing redundant component re-renders.
     *
     * @param {Object} payload - Partial update to bulkSelectionState
     * @param {number} [payload.selectedCount] - New selection count
     * @param {string|null} [payload.tsgKey] - New TSG key
     * @param {boolean} [payload.hasLock] - New lock state
     * @param {boolean} [payload.multiTsg] - New multi-TSG state
     * @param {boolean} [payload.isLoading] - New loading state
     */
    setBulkSelectionState(state, payload) {
      const next = {
        ...state.bulkSelectionState,
        ...payload,
      };

      if (
        state.bulkSelectionState.selectedCount === next.selectedCount &&
        state.bulkSelectionState.tsgKey === next.tsgKey &&
        state.bulkSelectionState.hasLock === next.hasLock &&
        state.bulkSelectionState.multiTsg === next.multiTsg &&
        state.bulkSelectionState.isLoading === next.isLoading
      ) {
        return;
      }

      state.bulkSelectionState = next;
    },

    resetState(state) {
      Object.assign(state, getInitialState());
    },

    upsertActiveTableFilter(state, filter) {
      filter.id = filter.id || uniqueId('filter');
      const existingFilter = find(state.activeTableFilters, { id: filter.id });
      // If editing, just override its props
      if (existingFilter) {
        return Object.assign(existingFilter, filter);
      }
      // Otherwise is a new filter to be added
      state.activeTableFilters = [...state.activeTableFilters, filter];
    },

    removeActiveTableFilter(state, filter) {
      state.activeTableFilters = state.activeTableFilters.filter(f => f.id !== filter.id);
    },

    setLoadingRecalculateAllResults(state, loading) {
      // Set to true at the start of run-all, set to false after fetching fresh ASR results
      state.loadingRecalculateAllResults = loading;
    },

    setRecalculatingAll(state, loading) {
      // Set to true at the start of run-all, set to false after engine finishes
      state.recalculatingAll = loading;

      if (loading) {
        state.loadingRecalculateAllResults = loading;
      }
    },

    setRefreshingAlerts(state, { workpackageId, isRefreshing }) {
      Vue.set(state.refreshingAlerts, workpackageId, isRefreshing);
    },

    setExpandedProduct(state, product) {
      state.expandedProduct = product;
    },

    setAlertFilter(state, alertType) {
      state.alertFilter = alertType;
    },

    setAlertCounts(state, alertCounts) {
      state.alertCounts = alertCounts;
    },

    setLoadingProductSettings(state, loading) {
      state.loadingProductSettings = loading;
    },

    setLoadingStoreGroupAggregations(state, loadingStoreGroupAggregations) {
      state.loadingStoreGroupAggregations = loadingStoreGroupAggregations;
    },

    addExpandedStoreGroupAggregation(state, { level, parentId, scenarioKey = null }) {
      const storeGroupAggregation = {
        level,
        parentId,
        scenarioKey,
        loading: true,
        data: [],
      };
      if (isEmpty(state.expandedStoreGroupAggregations)) {
        state.expandedStoreGroupAggregations = [storeGroupAggregation];
        return;
      }
      const firstSelected = state.expandedStoreGroupAggregations[0];
      if (firstSelected.parentId !== parentId || !scenarioKey) {
        state.expandedStoreGroupAggregations = [storeGroupAggregation];
        return;
      }
      state.expandedStoreGroupAggregations.push(storeGroupAggregation);
    },

    removeExpandedStoreGroupAggregation(state, { level, parentId, scenarioKey }) {
      const filterFunc = areStoreGroupAggregationsEqual({ level, parentId, scenarioKey });
      const aggIndex = state.expandedStoreGroupAggregations.findIndex(filterFunc);
      if (aggIndex === -1) return;
      state.expandedStoreGroupAggregations.splice(aggIndex, 1);
    },

    setExpandedStoreGroupAggregations(state, expandedStoreGroupAggregations) {
      state.expandedStoreGroupAggregations = expandedStoreGroupAggregations;
    },

    updateExpandedStoreGroupAggregation(
      state,
      { level, parentId, scenarioKey, loading = false, data = [], storeGroupOrder }
    ) {
      const filterFunc = areStoreGroupAggregationsEqual({ level, parentId, scenarioKey });
      const aggIndex = state.expandedStoreGroupAggregations.findIndex(filterFunc);
      if (aggIndex === -1) return;
      state.expandedStoreGroupAggregations.splice(aggIndex, 1, {
        level,
        parentId,
        scenarioKey,
        loading,
        data: mapStoreGroupAggregationsData({ data, products: state.items, storeGroupOrder }),
      });
    },

    setExpandedProductSettings(state, settings) {
      state.expandedProductSettings = settings;
    },

    clearCachedResult(state) {
      state.isCachedResult = false;
      state.cachedResultTimestamp = null;
    },

    setCachedResultTimestamp(state, timestamp) {
      state.isCachedResult = true;
      state.cachedResultTimestamp = timestamp;
    },

    setShowAgGridProductList(state, showAgGridProductList) {
      state.showAgGridProductList = showAgGridProductList;
    },

    setVisibleCompetitors(state, visibleCompetitors) {
      // Replaces the existing Set with the new one
      state.visibleCompetitors = visibleCompetitors;
    },

    setCurrentColumnState(state, columnState) {
      state.currentColumnState = columnState;
    },
  },

  actions: {
    async fetchLastTriggeredInfo(
      { commit },
      { workpackageId, scenarioKey, levelEntryKey, storegroupKeyNameMap, isGroupTSGsEnabled }
    ) {
      /**
       * Fetches last triggered engine/optimisation run information for scenario and TSG keys
       */
      if (!workpackageId || !scenarioKey || !levelEntryKey) {
        return;
      }

      const auditLogEventNames = {
        strategic_engine: 'engineTriggered',
        cross_elasticity_engine: 'optimisationTriggered',
      };

      // Set loading state for both engine types
      Object.keys(auditLogEventNames).forEach(engineType => {
        commit('setLastTriggeredLoading', { engineType, isLoading: true });
      });

      try {
        // Reset last triggered info
        commit('resetLastTriggeredInfo');

        // Fetch all scenario audit logs for both engine types in a single query
        const params = {
          where: {
            fullEventName: {
              $in: [
                Events.generic[auditLogEventNames.strategic_engine],
                Events.generic[auditLogEventNames.cross_elasticity_engine],
              ],
            },
            $and: [
              { entityIds: workpackageId },
              { entityIds: levelEntryKey },
              { entityIds: scenarioKey },
            ],
          },
          sortBy: 'date',
          sortDirection: descending,
        };

        const { data: auditLogs } = await axios.get('/api/audit-log', { params });

        // Get TSG keys from storegroupKeyNameMap
        const tsgKeys =
          isGroupTSGsEnabled && storegroupKeyNameMap ? Object.keys(storegroupKeyNameMap) : [];

        // Loop through each case and find the first matching log
        Object.keys(auditLogEventNames).forEach(engineType => {
          const eventName = Events.generic[auditLogEventNames[engineType]];

          /** Returns true if the log entry represents a full engine run (skipOptimisation !== true). */
          const isFullEngineRun = log =>
            !isAuditLogFlagTrue({ auditLogEntry: log, key: 'skipOptimisation' });

          // Find scenario-level run (no TSG key)
          const scenarioLog = auditLogs.find(
            log =>
              log.fullEventName === eventName &&
              !get(log, 'entityNames', []).includes('toolStoreGroupKey[0]')
          );
          if (scenarioLog) {
            commit('setLastTriggeredInfo', {
              engineType,
              key: scenarioKey,
              data: scenarioLog,
            });
          }

          // Only track full (non-skip) runs for the strategic engine
          const isStrategicEngine = engineType === 'strategic_engine';

          // Find most recent scenario-level full engine run (skipOptimisation: false)
          const scenarioFullLog =
            isStrategicEngine &&
            auditLogs.find(
              log =>
                log.fullEventName === eventName &&
                !get(log, 'entityNames', []).includes('toolStoreGroupKey[0]') &&
                isFullEngineRun(log)
            );
          if (scenarioFullLog) {
            commit('setLastTriggeredInfo', {
              engineType: 'strategic_engine_full_run',
              key: scenarioKey,
              data: scenarioFullLog,
            });
          }

          // Find TSG-specific runs for each TSG key
          tsgKeys.forEach(tsgKey => {
            const tsgLog = auditLogs.find(
              log =>
                log.fullEventName === eventName &&
                get(log, 'entityNames', []).includes('toolStoreGroupKey[0]') &&
                get(log, 'entityIds', []).includes(tsgKey)
            );

            // Use the most recent log between TSG-specific and scenario-level runs
            if (tsgLog && scenarioLog) {
              const mostRecentLog =
                new Date(tsgLog.date) >= new Date(scenarioLog.date) ? tsgLog : scenarioLog;
              commit('setLastTriggeredInfo', {
                engineType,
                key: tsgKey,
                data: mostRecentLog,
              });
            } else {
              commit('setLastTriggeredInfo', {
                engineType,
                key: tsgKey,
                data: tsgLog || scenarioLog || {},
              });
            }

            if (!isStrategicEngine) return;

            // Find the most recent full engine run for this TSG key
            const tsgFullLog = auditLogs.find(
              log =>
                log.fullEventName === eventName &&
                get(log, 'entityNames', []).includes('toolStoreGroupKey[0]') &&
                get(log, 'entityIds', []).includes(tsgKey) &&
                isFullEngineRun(log)
            );

            // Use the most recent full run between TSG-specific and scenario-level
            if (tsgFullLog && scenarioFullLog) {
              const mostRecentFullLog =
                new Date(tsgFullLog.date) >= new Date(scenarioFullLog.date)
                  ? tsgFullLog
                  : scenarioFullLog;
              commit('setLastTriggeredInfo', {
                engineType: 'strategic_engine_full_run',
                key: tsgKey,
                data: mostRecentFullLog,
              });
            } else if (tsgFullLog || scenarioFullLog) {
              commit('setLastTriggeredInfo', {
                engineType: 'strategic_engine_full_run',
                key: tsgKey,
                data: tsgFullLog || scenarioFullLog,
              });
            }
          });
        });
      } catch (error) {
        console.error('Error fetching last triggered info:', error);
        commit('resetLastTriggeredInfo');
      } finally {
        // Clear loading state for both engine types
        Object.keys(auditLogEventNames).forEach(engineType => {
          commit('setLastTriggeredLoading', { engineType, isLoading: false });
        });
      }
    },

    async fetchDataRefreshHistory({ commit, rootState }) {
      /**
       * Fetches data refresh history for the current workpackage.
       * Queries audit log for workpackage:refresh events, grouped by date (max 15 runs).
       */
      try {
        const workpackageId = rootState.workpackages.selectedWorkpackage._id;
        if (!workpackageId) return;

        const [err, res] = await to(
          axios.get('/api/audit-log', {
            params: {
              where: JSON.stringify({
                fullEventName: Events.workpackage.refresh,
                entityIds: { $in: [workpackageId] },
              }),
              sortBy: 'date',
              sortDirection: 'descending',
              limit: 15,
            },
          })
        );

        if (err) {
          console.error('Error fetching data refresh history:', err);
          commit('setDataRefreshHistory', []);
          return;
        }

        const { data: historyData } = res;
        commit('setDataRefreshHistory', historyData || []);
      } catch (error) {
        console.error('Error fetching data refresh history:', error);
        commit('setDataRefreshHistory', []);
      }
    },

    async fetchEngineRunHistory({ commit, rootState }) {
      /**
       * Fetches engine run history for the current workpackage.
       * Tracks generic:engine-triggered (SEO / KPI recalculate) and
       * generic:optimisation-triggered (XEO) events. Entries where setIntentionPrice is true
       * represent a workpackage created without optimisation and are excluded from the list.
       */
      try {
        const workpackageId = rootState.workpackages.selectedWorkpackage._id;
        if (!workpackageId) return;

        const eventTypes = [Events.generic.engineTriggered, Events.generic.optimisationTriggered];

        const [err, res] = await to(
          axios.get('/api/audit-log', {
            params: {
              where: JSON.stringify({
                fullEventName: { $in: eventTypes },
                entityIds: { $in: [workpackageId] },
              }),
              sortBy: 'date',
              sortDirection: 'descending',
              limit: 15,
            },
          })
        );

        if (err) {
          console.error('Error fetching engine run history:', err);
          commit('setEngineRunHistory', []);
          return;
        }

        // Ignore entries with setIntentionPrice flag as they represent workpackage creation without optimisation
        const filteredHistory = (res.data || []).filter(
          entry => !isAuditLogFlagTrue({ auditLogEntry: entry, key: 'setIntentionPrice' })
        );
        commit('setEngineRunHistory', filteredHistory);
      } catch (error) {
        console.error('Error fetching engine run history:', error);
        commit('setEngineRunHistory', []);
      }
    },

    async fetchRootHierarchyLevelItemId({ rootState, commit }) {
      commit('setLoading', true);
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err, res] = await to(
        axios.get(`/api/hierarchy/workpackage/${workpackageId}/root-hierarchy-item`)
      );
      if (err) {
        commit('setLoading', false);
        throw new Error(err.message);
      }
      const { data: rootHierarchyLevelItem } = res;
      commit('setRootHierarchyLevelItemId', { rootHierarchyLevelItem });
      commit('setLoading', false);
    },

    async checkRecalculatingAll({ rootState, commit }) {
      const [err, res] = await to(
        axios.get(
          `/api/pricing-engine/recalculating-all/workpackage/${
            rootState.workpackages.selectedWorkpackage._id
          }`
        )
      );
      if (err) {
        commit('setRecalculatingAll', false);
        throw new Error(err.message);
      }
      const { data: recalculatingAll } = res;
      commit('setRecalculatingAll', recalculatingAll);
    },

    async checkRecalculatingPgs({ commit }, { workpackageId, levelEntryKey }) {
      const [err, res] = await to(
        axios.get(
          `/api/pricing-engine/recalculating-pgs/workpackage/${workpackageId}/${levelEntryKey}`
        )
      );
      if (err) {
        commit('setCalculatingPgResults', {
          workpackageId,
          levelEntryKey,
          isCalculating: false,
        });
        throw new Error(err.message);
      }
      const { data: isCalculating } = res;
      commit('setCalculatingPgResults', {
        workpackageId,
        levelEntryKey,
        isCalculating,
      });
    },

    async checkRefreshingAlerts({ rootState, commit }) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err, res] = await to(
        axios.get(`/api/pricing-engine/refresh-alerts/workpackage/${workpackageId}`)
      );
      if (err) {
        commit('setRefreshingAlerts', { workpackageId, isRefreshing: false });
        throw new Error(err.message);
      }
      const { data: isRefreshing } = res;
      commit('setRefreshingAlerts', { workpackageId, isRefreshing });
    },

    async getRecalculatingSpecificPricingGroups({ commit, rootState }) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      try {
        const [err, res] = await to(
          axios.get(`/api/pricing-engine/specific-pricing-groups/workpackage/${workpackageId}`)
        );
        if (err) {
          throw new Error(err.message);
        }

        const { data: recalculatingSpecificPgs } = res;

        if (recalculatingSpecificPgs.length === 0) {
          commit('setCalculatingSpecificPgResults', {
            workpackageId,
            isCalculating: false,
          });
        } else {
          const pgLevelEntryKeys = recalculatingSpecificPgs.flatMap(
            entry => entry.params.level_entry_keys
          );
          commit('setCalculatingSpecificPgResults', {
            workpackageId,
            pgLevelEntryKeys,
            isCalculating: true,
          });
        }
      } catch (error) {
        console.error('Error in getRecalculatingSpecificPricingGroups:', error);
        commit('setCalculatingSpecificPgResults', {
          workpackageId,
          isCalculating: false,
        });
      }
    },

    async updateColumnStateWithOptionalRefetch(
      { commit, dispatch, state },
      { columnState, levelEntryKey, isUnitManagerView = false }
    ) {
      const previousColumnState = state.currentColumnState;

      if (columnState) {
        commit('setCurrentColumnState', columnState);
      }

      // Only refetch data if we have existing items and the column state has meaningfully changed
      // This prevents unnecessary API calls when columns are just reordered or visibility is switched off
      if (state.items.length !== 0) {
        const effectiveColumnState = columnState || state.currentColumnState;
        if (effectiveColumnState) {
          const shouldRefetch = agGridUtils.shouldRefetchForColumnStateChange(
            previousColumnState,
            effectiveColumnState
          );
          if (!shouldRefetch) {
            return;
          }
        }
      }
      await dispatch('fetchItems', { levelEntryKey, isUnitManagerView });
    },

    async fetchItems(
      { rootState, rootGetters, commit, state, getters, dispatch },
      { levelEntryKey, isUnitManagerView = false }
    ) {
      if (!levelEntryKey) return;

      commit('setLoadingItems', true);

      try {
        const { retailAttributesFilter } = rootState.filters;
        if (rootState.filters.scenario !== null || isUnitManagerView) {
          const formattedAttributeFilters = formatFilters({
            where: retailAttributesFilter,
            rootState,
          });
          await fetchItemsWithLimit(
            { rootState, rootGetters, commit, state, getters, dispatch },
            { levelEntryKey, formattedAttributeFilters, isUnitManagerView }
          );
        } else {
          console.warn('fetchItems called without scenario or unit manager');
        }
      } catch (e) {
        console.error('Error fetching items: ', e);
        commit('setItems', { items: [] });
      } finally {
        // Only set loading to false after everything completes or if there's an error
        commit('setLoadingItems', false);
      }
    },

    async fetchFullProductInfo(
      { rootState, getters, commit },
      { productKey, scenarioKey, toolStoreGroupKey }
    ) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const competitorNames = getters.selectedCompetitorNames;
      const [err, { data: product }] = await to(
        axios.get(`/api/workpackage-product/workpackage/${workpackageId}/${productKey}`, {
          params: { scenarioKey, toolStoreGroupKey, competitorNames },
        })
      );
      if (err) console.error('Error fetching full product info: ', err);
      commit('setExpandedProduct', product);
      return product;
    },

    async fetchProductSettings(
      { rootState, commit },
      { productKey, scenarioKey, toolStoreGroupKey }
    ) {
      commit('setLoadingProductSettings', true);
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err, { data: productSettings }] = await to(
        axios.get(
          `/api/product-settings/workpackage/${workpackageId}/scenario/${scenarioKey}/product/${productKey}`,
          { params: { toolStoreGroupKey } }
        )
      );
      if (err) {
        commit('setLoadingProductSettings', false);
        throw new Error(err.message);
      }
      commit('setLoadingProductSettings', false);
      commit('setExpandedProductSettings', productSettings[0]);
      return productSettings[0];
    },

    compareAggregations({ state, getters }, { level, parentId }) {
      const hierarchyKey = makeHierarchyKey(level, parentId, getters.competitors);
      const fetchedHierarchy = state.hierarchy[hierarchyKey];
      fetchedHierarchy.forEach(aggregation => {
        const levelEntryKey = aggregation.levelEntryKey;
        const name = aggregation.name;
        const groupedHierarchyKey = makeGroupedHierarchyKey(getters.competitors);
        const groupedAggregation =
          state.groupedHierarchy[groupedHierarchyKey][level][levelEntryKey];
        console.log(
          `Comparing aggregations for level: ${level} and levelEntryKey: ${levelEntryKey} ${name}`
        );
        objectDifferenceLogger(aggregation, groupedAggregation, ['parentId']);
      });
    },

    async fetchAggregatedHierarchyLevelItems(
      { commit, state, getters, rootState, dispatch },
      {
        aggregationLevel = 0,
        parentId,
        aggregateScenarios = false,
        aggregateStoreGroups = false,
        pricingGroupId,
        scenarioKey,
      } = {}
    ) {
      if (aggregateScenarios && !pricingGroupId) return;
      const getGroupedAggregationsFlag = rootState.clientConfig.toggleLogic[getGroupedAggregations];
      const getSingleAggregationsFlag = rootState.clientConfig.toggleLogic[getSingleAggregations];
      commit('setLoading', true);
      if (!aggregateScenarios && !aggregateStoreGroups) {
        commit('setAggregationLoadings', {
          level: aggregationLevel,
          aggregateStoreGroups,
          isLoading: true,
        });
      }
      let formattedAttributeFilters = {};
      const allTimeFlexiblePeriodsLoadingRequired =
        rootState.clientConfig.toggleLogic[displayHistoricalDropdown];

      const { retailAttributesFilter } = rootState.filters;
      if (retailAttributesFilter.length) {
        formattedAttributeFilters = formatFilters({
          where: retailAttributesFilter,
          rootState,
        });
      }

      if (isEmpty(state.selectedCompetitors)) {
        await dispatch('competitorMetadata/fetchCompetitorMetadata', null, { root: true });
      }
      const extraParams = getExtraFetchHierarchyItemAggregationParams(state, getters, {
        aggregationLevel,
        parentId,
        aggregateScenarios,
        aggregateStoreGroups,
        pricingGroupId,
        scenarioKey,
        historicalPeriod: state.selectedHistoricalPeriod,
        allTimeFlexiblePeriodsLoadingRequired,
      });

      const basicMutationParams = {
        level: aggregationLevel,
        aggregateStoreGroups,
      };
      const previousRequestCancellerKey = getHierarchyLevelAggregationRequestKey(
        basicMutationParams
      );
      if (has(state.timeFlexibleAggregationRequestsCanceller, previousRequestCancellerKey)) {
        state.timeFlexibleAggregationRequestsCanceller[previousRequestCancellerKey].cancel();
      }
      const axiosSource = axios.CancelToken.source();

      const params = {
        workpackageId: rootState.workpackages.selectedWorkpackage._id,
        competitorNames: getters.selectedCompetitorNames, // mongo-specific
        competitors: getters.competitors, // databricks-specific
        attributeFilters: formattedAttributeFilters,
        alertFilter: state.alertFilter,
        ...extraParams,
      };

      commit('setTimeFlexibleAggregationsLoading', {
        ...basicMutationParams,
        isLoading: true,
      });
      commit('setTimeFlexibleAggregationRequestsCanceller', {
        ...basicMutationParams,
        canceller: axiosSource,
      });
      const [err, response] = await to(
        axios.post(
          '/api/workpackage-product/aggregated',
          { ...params },
          { cancelToken: axiosSource.token }
        )
      );
      if (!aggregateScenarios && !aggregateStoreGroups) {
        commit('setAggregationLoadings', {
          level: aggregationLevel,
          aggregateStoreGroups,
          isLoading: false,
        });
      }

      if (err) {
        if (axios.isCancel(err)) {
          return;
        }
        commit('setLoading', false);
        commit('setTimeFlexibleAggregationsLoading', {
          ...basicMutationParams,
          isLoading: false,
        });
        // do not remove the canceller if the current request is cancelled
        // as it has been replaced and removing it will prevent future requests
        // from being cancelled leading to data mismatch for the filters
        commit('removeTimeFlexibleAggregationRequestsCanceller', {
          ...basicMutationParams,
        });
        throw new Error(err.message);
      }

      const { data: items } = response;

      commit('setLoading', false);
      commit('setTimeFlexibleAggregationsLoading', {
        ...basicMutationParams,
        isLoading: false,
      });
      commit('removeTimeFlexibleAggregationRequestsCanceller', {
        ...basicMutationParams,
      });

      if (!aggregateScenarios && !aggregateStoreGroups) {
        commit('setHierarchyLevelItems', {
          items,
          aggregationLevel,
          parentId: params.parentId,
          selectedCompetitors: getters.competitors,
        });
        if (getGroupedAggregationsFlag && getSingleAggregationsFlag) {
          dispatch('compareAggregations', {
            level: aggregationLevel,
            parentId: params.parentId,
          });
        }
      }
      return { items, preventUpdate: false };
    },

    async fetchGroupedAggregatedHierarchyLevelItems(
      { commit, state, getters, rootState, dispatch },
      { aggregationLevel = 0, parentId, refreshCache = true, loadingMsg, historicalPeriod } = {}
    ) {
      commit('clearCachedResult');

      // both yearly and time flexible fields will be fetched at the same time
      commit('setLoadingWithMsg', loadingMsg);
      const basicMutationParams = {
        level: aggregationLevel,
        aggregateStoreGroups: false,
      };
      const previousRequestCancellerKey = getHierarchyLevelAggregationRequestKey(
        basicMutationParams
      );

      if (has(state.timeFlexibleAggregationRequestsCanceller, previousRequestCancellerKey)) {
        state.timeFlexibleAggregationRequestsCanceller[previousRequestCancellerKey].cancel();
      }

      let formattedAttributeFilters = {};

      const { retailAttributesFilter } = rootState.filters;
      if (retailAttributesFilter.length) {
        formattedAttributeFilters = formatFilters({
          where: retailAttributesFilter,
          rootState,
        });
      }

      if (isEmpty(state.selectedCompetitors)) {
        await dispatch('competitorMetadata/fetchCompetitorMetadata', null, { root: true });
      }
      const extraParams = getExtraFetchHierarchyItemAggregationParams(state, getters, {
        aggregationLevel,
        parentId,
        aggregateScenarios: false,
        aggregateStoreGroups: false,
        historicalPeriod: historicalPeriod || state.selectedHistoricalPeriod,
      });
      const params = {
        workpackageId: rootState.workpackages.selectedWorkpackage._id,
        competitorNames: getters.selectedCompetitorNames, // mongo-specific
        competitors: getters.competitors, // databricks-specific
        attributeFilters: formattedAttributeFilters,
        alertFilter: state.alertFilter,
        ...extraParams,
      };
      const headers = refreshCache ? { 'refresh-cache': true } : {};
      const axiosSource = axios.CancelToken.source();
      commit('setTimeFlexibleAggregationsLoading', {
        ...basicMutationParams,
        isLoading: true,
      });
      commit('setTimeFlexibleAggregationRequestsCanceller', {
        ...basicMutationParams,
        canceller: axiosSource,
      });

      const [err, response] = await to(
        axios.get('/api/workpackage-product/aggregated/grouped', {
          params,
          cancelToken: axiosSource.token,
          headers,
        })
      );

      if (err) {
        if (axios.isCancel(err)) {
          return;
        }
        commit('setLoading', false);
        commit('setTimeFlexibleAggregationsLoading', {
          ...basicMutationParams,
          isLoading: false,
        });
        // do not remove the canceller if the current request is cancelled
        // as it has been replaced and removing it will prevent future requests
        // from being cancelled leading to data mismatch for the filters
        commit('removeTimeFlexibleAggregationRequestsCanceller', {
          ...basicMutationParams,
        });
        throw new Error(err.message);
      }

      commit('setLoading', false);
      commit('setTimeFlexibleAggregationsLoading', {
        ...basicMutationParams,
        isLoading: false,
      });
      commit('removeTimeFlexibleAggregationRequestsCanceller', {
        ...basicMutationParams,
      });

      const { data: items } = response;
      const {
        'cached-result': isCachedResult,
        'cached-result-timestamp': cacheTimestamp,
      } = response.headers;
      if (isCachedResult) {
        commit('setCachedResultTimestamp', cacheTimestamp);
      }

      commit('setGroupedHierarchyLevelItems', {
        items,
        selectedCompetitors: getters.competitors,
      });
      // Reset temporary flag when hierarchy is updated
      commit('setTemporaryUnsavedKpiResults', false);

      return { items };
    },

    async downloadItems(
      context,
      { isUnitManagerView, translationMap = {}, columnFormatters = {}, selectedCompetitorKeys = [] }
    ) {
      const { commit, rootState, state, getters } = context;
      commit('setDownloadingItems', true);
      // note this is not a job, just matching required fields for generic notification
      const jobId = 'download_grid_view';
      const { finished, failed } = jobStatuses;

      let levelEntryDescription = '';

      try {
        const scenarioKey = rootState.filters.scenario;
        const workpackageId = rootState.workpackages.selectedWorkpackage._id;
        const levelEntryKey = isUnitManagerView
          ? rootState.filters.selectedLevelEntryKey
          : rootState.filters.pricingGroup;

        const { retailAttributesFilter } = rootState.filters;
        let formattedAttributeFilters = {};
        if (retailAttributesFilter.length) {
          formattedAttributeFilters = formatFilters({
            where: retailAttributesFilter,
            rootState,
          });
        }
        const useStoreGroupChainsFlag = rootState.clientConfig.toggleLogic[useStoreGroupChains];
        const competitors = useStoreGroupChainsFlag
          ? getters.competitors
          : union(Array.from(state.visibleCompetitors), getters.competitors);

        const params = {
          export: true,
          formatForExport: true,
          attributeFilters: formattedAttributeFilters,
          translationMap,
          activeTableFilters: state.activeTableFilters,
          competitors, // databricks-specific
          alertFilter: state.alertFilter,
          selectedHistoricalPeriod: state.selectedHistoricalPeriod,
          // Export only/ Default selectedCompetitorKeys to the visible competitors if none selected or modal disabled
          selectedCompetitorKeys: !isEmpty(selectedCompetitorKeys)
            ? selectedCompetitorKeys
            : competitors,
          toolStoreGroupCompetitors: useStoreGroupChainsFlag
            ? Array.from(state.visibleCompetitors)
            : [],
        };

        const hierarchyEntry = await axios.get(`/api/hierarchy/levelEntryKey/${levelEntryKey}`);
        levelEntryDescription = hierarchyEntry.data.levelEntryDescription;

        const baseUrl = `/api/workpackage-product/workpackage/${workpackageId}/gridview/${levelEntryKey}`;
        const url = isUnitManagerView ? `${baseUrl}/unit` : `${baseUrl}/scenario/${scenarioKey}`;
        const response = await axios.post(url, params);

        const { numberFormats, i18nconfig, exportConfigs } = rootState.clientConfig;
        const { overrideNumberFormat } = i18nconfig;
        const { rowsPerFile } = exportConfigs.exportToExcel;

        downloadXlsxFile(response.data, 'workpackage_product_data.xlsx', {
          rowsPerFile,
          columnFormatters,
          numberFormatsConfig: numberFormats[overrideNumberFormat],
        });
        triggerStatusNotification({
          context,
          jobId,
          jobStatus: finished,
          notificationPayload: levelEntryDescription,
        });
      } catch (e) {
        console.error(e);
        triggerStatusNotification({
          context,
          jobId,
          jobStatus: failed,
          notificationPayload: levelEntryDescription,
        });
      } finally {
        commit('setDownloadingItems', false);
      }
    },

    async checkPricingEngineStatus({ commit, rootState }, scenarioKey = null) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      scenarioKey = scenarioKey || rootState.filters.scenario;
      if (workpackageId && scenarioKey) {
        const [err, res] = await to(
          axios.get(
            `/api/pricing-engine/workpackage/${workpackageId}/scenario/${scenarioKey}/status`
          )
        );
        if (err) {
          commit('setCalculatingScenarioResults', false);
          commit('setCalculatingTsgStrategicKeys', []);
          throw new Error(err.message);
        }
        const { data: engineIsRunning } = res;
        commit('setCalculatingScenarioResults', engineIsRunning);
        if (!engineIsRunning) {
          // Clear per-TSG keys — engine is no longer running so no TSG can be active.
          // Guards against stale keys if the completion handler was missed (e.g. WebSocket disconnect).
          commit('setCalculatingTsgStrategicKeys', []);
        }
      }
    },

    async runPricingEngine({ commit }, { runParameters: kwargs }) {
      commit('setCalculatingScenarioResults', true);
      if (!isEmpty(kwargs.tool_store_group_keys)) {
        commit('setCalculatingTsgStrategicKeys', kwargs.tool_store_group_keys);
      }

      try {
        await axios.post('/api/pricing-engine/dag', {
          params: kwargs,
        });
      } catch (err) {
        commit('setCalculatingScenarioResults', false);
        commit('setCalculatingTsgStrategicKeys', []);
        console.info(err);
      }
    },

    async runEngineForSpecificPricingGroups({ rootState }, { pgKeys }) {
      if (isEmpty(pgKeys)) return;
      const selectedWorkpackageId = get(rootState.workpackages.selectedWorkpackage, '_id', null);

      const [err] = await to(
        axios.post('/api/pricing-engine/specific-pricing-groups', {
          params: {
            workpackage_id: selectedWorkpackageId,
            level_entry_keys: pgKeys,
          },
        })
      );

      if (err) {
        console.error(err);
      }

      return err === null;
    },

    async runEngineForPricingGroups({ rootState, commit }, { level = 1, levelEntryKey = null }) {
      if (!levelEntryKey) return;
      const selectedWorkpackageId = get(rootState.workpackages.selectedWorkpackage, '_id', null);
      commit('setCalculatingPgResults', {
        workpackageId: selectedWorkpackageId,
        levelEntryKey,
        isCalculating: true,
      });

      const [err] = await to(
        axios.post('/api/pricing-engine/pricing-groups', {
          params: {
            workpackage_id: selectedWorkpackageId,
            level,
            level_entry_key: levelEntryKey,
          },
        })
      );

      if (err) {
        commit('setCalculatingPgResults', {
          workpackageId: selectedWorkpackageId,
          levelEntryKey,
          isCalculating: false,
        });
      }
      return err === null;
    },

    async postRunEngineForPricingGroups(
      { rootState, rootGetters, getters, commit, dispatch },
      { payload }
    ) {
      const selectedWorkpackageId = get(rootState.workpackages.selectedWorkpackage, '_id', null);
      const [workpackageId, levelEntryKey] = payload.split(payloadKeySeparator);
      commit('setCalculatingPgResults', {
        workpackageId,
        levelEntryKey,
        isCalculating: false,
      });
      if (workpackageId !== selectedWorkpackageId) {
        return;
      }
      const unitIds = rootGetters['hierarchy/getApprovedUnitsForWorkpackage'];
      dispatch('getFailedRuns');
      await Promise.all([
        dispatch('fetchAggregations'),
        rootState.filters.selectedLevelEntryKey
          ? dispatch('fetchItems', {
              levelEntryKey: rootState.filters.selectedLevelEntryKey,
              isUnitManagerView: getters.isUnitManagerView,
            })
          : null,
        dispatch('scenarioMetadata/getAggregatedScenarios', {}, { root: true }),
        dispatch('getAlertCounts'),
        ...unitIds.map(unitId =>
          dispatch(
            'hierarchy/unsetUMApprovalIfPriceChanged',
            { levelEntryKey: unitId },
            { root: true }
          )
        ),
      ]);
    },

    // TODO: ensure all postRunEngine variants are consistent.
    // Approvals are reset, failed runs are updated, etc.
    async postRunEngineForSpecificPricingGroups({ dispatch, rootState, getters }) {
      await Promise.all([
        dispatch('getFailedRuns'),
        dispatch('getRecalculatingSpecificPricingGroups'),
        dispatch('fetchAggregations'),
        rootState.filters.selectedLevelEntryKey
          ? dispatch('fetchItems', {
              levelEntryKey: rootState.filters.selectedLevelEntryKey,
              isUnitManagerView: getters.isUnitManagerView,
            })
          : null,
      ]);
    },

    async runKPIsForAllScenarios({ rootState, state, commit }) {
      if (state.calculatingScenarioResults || state.calculatingScenarioElasticityResults) return;
      const selectedWorkpackageId = get(rootState.workpackages.selectedWorkpackage, '_id', null);
      commit('setRecalculatingAll', true);
      commit('setLoadingRecalculateAllResults', true);
      const [err] = await to(
        axios.post('/api/pricing-engine/all', {
          params: {
            workpackage_id: selectedWorkpackageId,
            skip_optimisation: true,
            skip_alerts: true, // explicitly set to true to get correct audit log message
          },
        })
      );

      if (err) {
        commit('setRecalculatingAll', false);
        commit('setLoadingRecalculateAllResults', false);
      }
      return err === null;
    },

    async runEngineForAllScenarios({ rootState, state, commit }) {
      if (state.calculatingScenarioResults || state.calculatingScenarioElasticityResults) return;
      const selectedWorkpackageId = get(rootState.workpackages.selectedWorkpackage, '_id', null);
      commit('setRecalculatingAll', true);
      commit('setLoadingRecalculateAllResults', true);
      const [err] = await to(
        axios.post('/api/pricing-engine/all', {
          params: {
            workpackage_id: selectedWorkpackageId,
          },
        })
      );

      if (err) {
        commit('setRecalculatingAll', false);
        commit('setLoadingRecalculateAllResults', false);
      }
      return err === null;
    },

    startRefreshingAlerts({ commit }, params) {
      const workpackageId = get(params, 'payload', null);
      commit('setRefreshingAlerts', { workpackageId, isRefreshing: true });
    },

    async runRefreshAlerts({ rootState, state, commit }) {
      if (state.calculatingScenarioResults || state.calculatingScenarioElasticityResults) return;
      const selectedWorkpackageId = get(rootState.workpackages.selectedWorkpackage, '_id', null);
      const [err] = await to(
        axios.post('/api/pricing-engine/refresh-alerts', {
          params: {
            workpackage_id: selectedWorkpackageId,
          },
        })
      );

      if (err) {
        commit('setRefreshingAlerts', {
          workpackageId: selectedWorkpackageId,
          isRefreshing: false,
        });
      }
      return err === null;
    },

    async postRunEngineForAllScenarios(
      { rootState, rootGetters, commit, dispatch, getters },
      { payload }
    ) {
      // possible issue with design here, we don't keep track of jobs per workpackage.
      // we rely on reloading components to check for running jobs,
      // but if we trigger engine on workpackage A and change to workpackage B and trigger it as well,
      // we don't want the spinner state on the current page (workpackage B) to update based on the first job, only the second job.
      // hence the early exit here if workapckageId doesn't match selectedWorkpackage.
      // ideally we'd track jobs based on ids, and always update state,
      // but this would make the vuex state much more complex.
      // e.g. state.workpackageId.recalculatingAll = true/false, instead of just state.recalculatingAll = true/false
      // expected payload is workpackage_id${payloadKeySeparator}skip_optimisation

      const workpackageId = payload.includes(payloadKeySeparator)
        ? payload.split(payloadKeySeparator)[0]
        : payload;
      const selectedWorkpackageId = get(rootState.workpackages.selectedWorkpackage, '_id', null);
      if (workpackageId !== selectedWorkpackageId) {
        return;
      }
      commit('setRecalculatingAll', false);
      const unitIds = rootGetters['hierarchy/getApprovedUnitsForWorkpackage'];
      dispatch('getFailedRuns');
      await Promise.all([
        dispatch('fetchAggregations'),
        rootState.filters.selectedLevelEntryKey
          ? dispatch('fetchItems', {
              levelEntryKey: rootState.filters.selectedLevelEntryKey,
              isUnitManagerView: getters.isUnitManagerView,
            })
          : null,
        dispatch('scenarioMetadata/getAggregatedScenarios', {}, { root: true }),
        dispatch('getAlertCounts'),
        ...unitIds.map(unitId =>
          dispatch(
            'hierarchy/unsetUMApprovalIfPriceChanged',
            { levelEntryKey: unitId },
            { root: true }
          )
        ),
      ]);
      commit('setLoadingRecalculateAllResults', false);
    },

    async postRunRefreshAlerts(
      { state, rootState, commit, dispatch, getters },
      { payload: workpackageId }
    ) {
      const selectedWorkpackageId =
        get(rootState.workpackages.selectedWorkpackage, '_id', null) || workpackageId;

      commit('setRefreshingAlerts', { workpackageId, isRefreshing: false });

      if (workpackageId !== selectedWorkpackageId) {
        return;
      }

      const isAlertsColumnVisible =
        state.currentColumnState &&
        state.currentColumnState.some(col => col.colId.includes('filteredAlerts') && !col.hide);

      await Promise.all([
        rootState.filters.selectedLevelEntryKey && isAlertsColumnVisible
          ? dispatch('fetchItems', {
              levelEntryKey: rootState.filters.selectedLevelEntryKey,
              isUnitManagerView: getters.isUnitManagerView,
            })
          : null,
        dispatch('getAlertCounts'),
      ]);
    },

    async getFailedRuns({ rootState, commit }) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err, { data }] = await to(
        axios.get(`/api/scenario-metadata/workpackage/${workpackageId}/failures`)
      );
      if (err) console.error(err);

      commit('setFailedRuns', data);
      return data;
    },

    async handleRecalculate(
      { dispatch },
      { scenarioKey, workpackageId, toolStoreGroupKeys = null } = {}
    ) {
      const params = {
        scenario_key: scenarioKey,
        workpackage_id: workpackageId,
        tool_store_group_keys: toolStoreGroupKeys,
      };
      await dispatch('runPricingEngine', { runParameters: params });
    },

    clearItems({ commit }) {
      commit('setItems', { items: [] });
    },

    setExpandedHierarchyLevelItems({ commit }, { newExpandedHierarchy }) {
      if (!newExpandedHierarchy) commit('initExpandedHierarchyLevelItems');
      else commit('setExpandedHierarchyLevelItems', newExpandedHierarchy);
    },

    resetExpandedHierarchyLevels({ commit }) {
      commit('resetExpandedHierarchyLevels');
    },

    async updateScenarioResults(
      { rootState, commit, dispatch, state },
      { jobStatus, payload: scenarioKey }
    ) {
      // if multiple run-pricing jobs are running, ensure we only reset loading state and fetch data for visible scenarios
      const availableScenariokeys = rootState.scenarioMetadata.scenarioMetadata.map(
        v => v.scenarioKey
      );
      if (!availableScenariokeys.includes(scenarioKey)) {
        return;
      }
      commit('setCalculatingScenarioResults', false);
      commit('setCalculatingTsgStrategicKeys', []);
      commit('setCalculatingScenarioElasticityResults', false);
      commit('setCalculatingTsgElasticityKeys', []);
      commit('scenarioMetadata/removeCalculating', scenarioKey, { root: true });
      const unitId = state.expandedHierarchyLevelItems[unitLevel + 1];
      await Promise.all([
        dispatch('getFailedRuns'),
        dispatch('scenarioMetadata/getAggregatedScenarios', {}, { root: true }),
        dispatch('getAlertCounts'),
        dispatch(
          'hierarchy/unsetUMApprovalIfPriceChanged',
          { levelEntryKey: unitId },
          { root: true }
        ),
      ]);
      if (jobStatus === jobStatuses.finished) {
        await Promise.all([
          dispatch('fetchItems', { levelEntryKey: rootState.filters.pricingGroup }),
          dispatch('fetchAggregations'),
        ]);
        if (!isEmpty(state.expandedProduct)) {
          const productSpecificPromises = [
            dispatch('fetchFullProductInfo', {
              scenarioKey: state.expandedProduct.scenarioResults.scenarioKey,
              productKey: state.expandedProduct.article.productKey,
              toolStoreGroupKey: state.expandedProduct.toolStoreGroupKey,
            }),
          ];
          if (rootState.clientConfig.toggleLogic[displayRulesListView]) {
            productSpecificPromises.push(
              dispatch('fetchProductSettings', {
                scenarioKey: state.expandedProduct.scenarioResults.scenarioKey,
                productKey: state.expandedProduct.article.productKey,
                toolStoreGroupKey: state.expandedProduct.toolStoreGroupKey,
              })
            );
          }
          await Promise.all(productSpecificPromises);
        }
      }
    },

    async canEngineRun({ rootState, commit }, { pricingGroupId }) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;

      const [err, response] = await to(
        axios.get(
          `/api/attributes/workpackage/${workpackageId}/pricingGroup/${pricingGroupId}/canEngineRun`
        )
      );

      if (err) {
        throw new Error(err.message);
      }
      commit('setCanEngineRunResults', response.data);
      return response.data;
    },

    async updateOverridePrice({ getters, dispatch }, { item, items } = {}) {
      const itemsToProcess = items || (item ? [item] : []);

      if (isEmpty(itemsToProcess)) {
        throw new Error('No items provided for override price update.');
      }

      if (!isEmpty(items) && !getters.useNewPriceOverridesAPI) {
        throw new Error(
          'Multiple items in updateOverridePrice params but useNewPriceOverridesAPI is false.'
        );
      }

      if (getters.useNewPriceOverridesAPI) {
        await dispatch('updateOverridePriceNew', {
          items: itemsToProcess,
        });
        return;
      }

      await dispatch('updateOverridePriceOld', {
        item: itemsToProcess[0],
      });
    },

    /**
     * Updates price overrides using the new batched API.
     *
     * This action orchestrates multiple grid-related side effects beyond just updating prices:
     * 1. Updates products in state.items array (commit 'updateItem')
     * 2. Triggers hierarchy aggregation updates (commit 'setHierarchyUnsavedKpiResults')
     * 3. Fetches expanded product details if product drawer is open
     * 4. Updates alert counts across the workpackage
     * 5. Coordinates with Unit Manager approval state
     *
     * Called by:
     * - updateOverridePrice (single item from grid edits/mixin)
     * - bulkPriceOverride mixin (multiple items from bulk operations)
     *
     * @param {Array<Object>} items - Array of product items with override data
     * @param {string} items[].productKey - Product identifier
     * @param {string} items[].toolStoreGroupKey - Store group identifier
     * @param {Object} items[].overridePrice - Override price object with price, dates, flags
     * @param {string} items[].workpackageId - Workpackage identifier
     * @param {string} items[].scenarioKey - Scenario identifier
     * @param {number} [items[].startingPrice] - Starting price for KPI calculation
     * @param {boolean} [items[].revert] - Whether this is a revert operation
     * @param {boolean} [items[].usePromoPriceOnSmartOverride] - Use promo price for smart overrides
     * @throws {Error} If items array is empty or API call fails
     */
    async updateOverridePriceNew({ commit, rootState, state, getters, dispatch }, { items }) {
      if (isEmpty(items)) {
        throw new Error('No items provided for override price update.');
      }
      // Workpackage data
      const goLiveDate = rootState.workpackages.selectedWorkpackage.goLiveDate;
      const analyticsIncludingVat =
        rootState.workpackages.selectedWorkpackage.analyticsIncludingVat;
      const forecastVolumes = rootState.clientConfig.toggleLogic[enableVolumeForecast];
      // Reverts not allowed if volume forecast is enabled due to multiple products being affected
      const revertAllowed = !forecastVolumes;
      const { workpackageId, scenarioKey } = items[0];

      const products = {};
      items.forEach(item => {
        const overridePrice = {
          ...item.overridePrice,
          toBeExpired: getters.isOverrideToBeExpired(item.overridePrice),
        };
        const startingPrice = item.startingPrice;
        const scenarioPriceForCalculation = computeScenarioPrice({
          item,
          goLiveDate,
          usePromoPriceOnSmartOverride: item.usePromoPriceOnSmartOverride,
          intentionPrice: startingPrice,
          xeoEnabled: rootState.clientConfig.toggleLogic[enablePriceOptimisation],
        });

        const product = {
          product_key: item.productKey,
          tool_store_group_key: item.toolStoreGroupKey,
          override_price: overridePrice,
          scenario_price: scenarioPriceForCalculation,
          starting_price: startingPrice,
          revert: item.revert && revertAllowed,
        };
        if (!products[item.toolStoreGroupKey]) {
          products[item.toolStoreGroupKey] = {};
        }
        products[item.toolStoreGroupKey][item.productKey] = product;
      });

      const [err, response] = await to(
        axios.post(
          `/api/price-overrides/${scenarioKey}/workpackage/${workpackageId}/gridview/override`,
          {
            products,
            analytics_including_vat: analyticsIncludingVat,
          }
        )
      );
      if (err) {
        const jobId = 'override_prices';
        const baseTranslation = `notifications.${jobId}`;
        const jobStatus = 'failed';
        // Log up to 5 productKeys in notification
        let notificationPayload = '';
        const productKeys = items.map(i => i.productKey).slice(0, 5);
        notificationPayload = productKeys.join(', ');
        if (items.length > 5) {
          notificationPayload += ', ...';
        }
        const notification = {
          // ensure id is unique so message is always displayed.
          id: `${jobId}:${jobStatus}:${Date.now()}`,
          jobStatus,
          notificationPayload,
          baseTranslation,
        };
        commit('notifications/addNotification', notification, { root: true });
        throw new Error(err.response.data);
      }

      const unitIds = [];
      await Promise.all(
        response.data.map(async data => {
          let item = items.find(
            i => i.productKey === data.productKey && i.toolStoreGroupKey === data.toolStoreGroupKey
          );

          if (!item) {
            // If item is not present in request, get from state
            item = state.items.find(
              i =>
                i.productKey === data.productKey && i.toolStoreGroupKey === data.toolStoreGroupKey
            );
          }

          updateProductWithUnsavedKpiResults({
            product: item,
            kpiResult: data.scenarioResults,
            alerts: data.alerts,
            readFromDatabricks: getters.readFromDatabricks,
            isAgGridProductListEnabled: getters.isAgGridProductListEnabled,
          });

          // Do not mutate item after this point
          // both Vue.set calls required to ensure hasUnsavedKpiResults reactivity
          const isRevert = item.revert && revertAllowed;
          Vue.set(item, 'hasUnsavedKpiResults', !isRevert);
          // Preserve existing hasPriceChanged: true for sim-group bystanders whose
          // price was directly overridden in a prior call. Only a revert may clear it.
          Vue.set(
            item,
            'hasPriceChanged',
            (data.hasPriceChanged || item.hasPriceChanged) && !isRevert
          );
          commit('updateItem', { updateItem: item });

          // Update hierarchy aggregations to reflect override state
          // Hierarchy shows orange indicators when pricing groups have unsaved overrides.
          // We only update ancestors when creating first override or reverting last override
          // in a pricing group to minimise unnecessary hierarchy refreshes.
          const pgId = get(item, `hierarchy.${pricingGroupLevel}.id`);
          const overridesInPricingGroup = state.items.filter(
            i => get(i, `hierarchy.${pricingGroupLevel}.id`) === pgId && i.hasUnsavedKpiResults
          );
          // only update parent hierarchy override state when reverting last override, or creating first override in PG
          const minOverrides = isRevert ? 0 : 1;
          if (overridesInPricingGroup.length <= minOverrides) {
            // update higher hierarchies with pending save kpi results
            commit('setHierarchyUnsavedKpiResults', {
              hierarchyKey: pgId,
              selectedCompetitors: getters.competitors,
              revert: isRevert,
            });
          }

          if (
            !isEmpty(state.expandedProduct) &&
            state.expandedProduct.article.productKey === item.productKey &&
            state.expandedProduct.scenarioResults.scenarioKey === item.scenarioKey
          ) {
            await dispatch('fetchFullProductInfo', {
              scenarioKey: state.expandedProduct.scenarioResults.scenarioKey,
              productKey: state.expandedProduct.article.productKey,
              toolStoreGroupKey: state.expandedProduct.toolStoreGroupKey,
            });
          }

          unitIds.push(item.hierarchy[unitLevel].id);
        })
      );

      // Notify the user when any override was silently removed because it will expire before
      // the go-live date. The Python layer sets overrideExpired: true on those results and
      // clears the overridePrice from the scenarioResults so the grid is consistent with
      // what the engine will produce at go-live.
      const expiredResults = response.data.filter(d => d.overrideExpired);
      if (expiredResults.length > 0) {
        const expiredJobId = 'override_price_expired';
        const expiredJobStatus = 'warning';
        // python layer return product_key, but we need productKeyDisplay for FE
        const expiredProductKeys = expiredResults
          .map(d => {
            const matchedItem = state.items.find(
              i => i.productKey === d.productKey && i.toolStoreGroupKey === d.toolStoreGroupKey
            );
            return get(matchedItem, 'productKeyDisplay');
          })
          .slice(0, 5)
          .join(', ');
        const expiredNotificationPayload =
          expiredResults.length > 5 ? `${expiredProductKeys}, ...` : expiredProductKeys;
        commit(
          'notifications/addNotification',
          {
            id: `${expiredJobId}:${expiredJobStatus}:${Date.now()}`,

            jobStatus: expiredJobStatus,
            notificationPayload: expiredNotificationPayload,
            baseTranslation: `notifications.${expiredJobId}`,
          },
          { root: true }
        );
      }

      // Refresh alert counts and Unit Manager approval state.
      // Alerts may change based on new override prices (e.g., margin alerts).
      // Unit Manager approvals are invalidated when prices change.
      dispatch('getAlertCounts');
      unitIds.forEach(unitId => {
        dispatch(
          'hierarchy/unsetUMApprovalIfPriceChanged',
          { levelEntryKey: unitId },
          { root: true }
        );
      });

      // Update price validations from inline override response data.
      // The Python validation engine runs as part of the override flow and returns
      // validation results per product. We merge these directly into the Vuex store
      // instead of making a separate API call, avoiding an unnecessary round-trip.
      // Use the originally-overridden product keys (from `items`) rather than all
      // response product keys — the response may include similarity group products
      // that were not validated, and clearing their validations would incorrectly
      // remove existing failures until the next full re-fetch.
      if (
        rootState.clientConfig.priceValidations &&
        rootState.clientConfig.priceValidations.enabled
      ) {
        const overriddenProductKeys = items.map(i => i.productKey);
        const validations = response.data
          .filter(d => overriddenProductKeys.includes(d.productKey))
          .flatMap(d => (d.validations || []).map(v => ({ ...v, productKey: d.productKey })));
        commit(
          'priceValidations/mergeProductValidations',
          { productKeys: overriddenProductKeys, validations },
          { root: true }
        );
      }
    },

    /**
     * LEGACY: Updates price override using the old single-item API.
     *
     * This action is deprecated in favour of updateOverridePriceNew but maintained for
     * backwards compatibility during feature flag rollout. It performs full KPI recalculation
     * on the server side for each override.
     *
     * Same grid-related side effects as updateOverridePriceNew:
     * - Updates products in state.items
     * - Triggers hierarchy updates
     * - Refreshes expanded product details
     * - Updates alert counts
     * - Coordinates with Unit Manager state
     *
     * @param {Object} item - Product item with override data (single item only)
     * @deprecated Prefer updateOverridePriceNew for better performance
     */
    async updateOverridePriceOld({ commit, rootState, state, getters, dispatch }, { item }) {
      // Competitor prices from databricks, already pre-filtered on disabled == false
      const competitors = values(get(item, 'competitors', {})) // recalculate-kpis accepts competitors array https://owlabs.atlassian.net/browse/PRICE-4340?focusedCommentId=212723
        .map(comp => ({
          ...comp,
          competitorIndex: comp.competitorIndex ? comp.competitorIndex : null, // Ensure competitorIndex is always present, It's required by recalculate-kpis
        }));

      // Get competitor price data from databricks field to be used as kpi input
      const enabledCompetitorPrices = competitors
        .filter(c => c.competitorPrice > 0)
        .reduce((output, { competitorDescription, competitorPrice, avgCompetitorPrice }) => {
          output[competitorDescription] = {
            competitorPrice,
            avgCompetitorPrice,
          };
          return output;
        }, {});

      const readFromDatabricks = getters.readFromDatabricks;
      // startingPrice isObject when useFirstFuturePrice, isValidFuturePrice, and readFromDatabricks are true
      const transformedStartingPrice = isObject(item.startingPrice)
        ? item.startingPrice.price
        : item.startingPrice;

      const goLiveDate = rootState.workpackages.selectedWorkpackage.goLiveDate;
      const scenarioPriceForCalculation = computeScenarioPrice({
        item,
        goLiveDate,
        usePromoPriceOnSmartOverride: item.usePromoPriceOnSmartOverride,
        intentionPrice: transformedStartingPrice,
        xeoEnabled: rootState.clientConfig.toggleLogic[enablePriceOptimisation],
      });
      const analyticsIncludingVat =
        rootState.workpackages.selectedWorkpackage.analyticsIncludingVat;
      const { workpackageId, scenarioKey, toolStoreGroupKey } = item;
      const kwargs = {
        scenario_key: scenarioKey,
        workpackage_id: workpackageId,
        tool_store_group_key: toolStoreGroupKey,
      };

      const overridePrice = {
        ...item.overridePrice,
        toBeExpired: getters.isOverrideToBeExpired(item.overridePrice),
      };
      const kpiInput = {
        starting_price: transformedStartingPrice,
        live_price: readFromDatabricks ? get(item, 'livePrice.price') : item.livePrice,
        live_cost: readFromDatabricks ? get(item, 'liveCost.price') : item.liveCost,
        scenario_price: isObject(scenarioPriceForCalculation)
          ? scenarioPriceForCalculation.price
          : scenarioPriceForCalculation,
        forecast_volume: item.forecastVolume,
        yearly_volume: item.yearlyVolume,
        ...volumeKpiInputFields(item),
        non_promo_net_cost: get(item, 'mandatoryEngineInputs.nonPromoNetCost'),
        promo_participation_sales:
          get(item, 'mandatoryEngineInputs.promoParticipationSales', 0) / 100,
        promo_discount: get(item, 'mandatoryEngineInputs.promoDiscount', 0) / 100,
        promo_funding: get(item, 'mandatoryEngineInputs.promoFunding', 0) / 100,
        vat_rate: item.vatRate,
        vat_calc: item.vatCalc || analyticsIncludingVat ? item.vatRate : 0,
        competitors: enabledCompetitorPrices,
        content_value: item.contentValue ? item.contentValue : 0,
        norm_weight: isNumber(item.normWeight) ? item.normWeight : null,
        cross_elasticity_volume: item.crossElasticityVolume,
        cross_elasticity_price: item.crossElasticityPrice,
        strategic_forecast_volume: item.strategicForecastVolume,
        strategic_price: item.strategicPrice,
        static_forecast_volume: get(item, 'mandatoryEngineInputs.forecastVolume'),
      };

      const scenarioResultsForItem = {
        productKey: item.productKey,
        productKeyDisplay: item.productKeyDisplay,
        toolStoreGroupKey: item.toolStoreGroupKey,
        economicReferencePrice: item.economicReferencePrice,
        architectureReferencePrice: item.architectureReferencePrice,
        competitorReferencePrice: item.competitorReferencePrice,
        scenarioPrice: {
          price: item.scenarioPrice,
          pricePerNormWeight: item.scenarioPricePerNormWeight,
          pricePerContentUnit: item.scenarioPricePerContentUnit,
        },
        optimizedPrice: item.optimizedPrice,
        scenarioPriceChange: item.scenarioPriceChange,
        scenarioPriceChangeRatio: item.scenarioPriceChangeRatio,
        scenarioSalesImpact: item.scenarioSalesImpact,
        regularSalesImpact: item.regularSalesImpact,
        scenarioCostImpact: item.scenarioCostImpact,
        regularCostImpact: item.regularCostImpact,
        scenarioMargin: item.scenarioMargin,
        overridePrice,
        competitors,
        scenarioKey: item.scenarioKey,
        workpackageId: item.workpackageId,
        competitorPenalty: item.competitorPenalty,
        economicPenalty: item.economicPenalty,
        architecturePenalty: item.architecturePenalty,
        priceRange: item.priceRange,
        alerts: item.alerts,
        similarityGroupKey: item.similarityGroupKey,
        cannGroupId: item.cannGroupId,
        lastEngineSource: item.lastEngineSource,
      };
      // call recalculate...
      const [err, response] = await to(
        axios.post(`/api/scenario-results/${kwargs.scenario_key}/recalculate-kpis`, {
          kpiInput,
          scenarioResults: scenarioResultsForItem,
          kwargs,
          analyticsIncludingVat,
          revert: item.revert,
        })
      );
      if (err) {
        const jobId = 'recalculate_kpis';
        const baseTranslation = `notifications.${jobId}`;
        const jobStatus = 'failed';
        const notification = {
          // ensure id is unique so message is always displayed.
          id: `${jobId}:${jobStatus}:${Date.now()}`,
          jobStatus,
          notificationPayload: item.productKey,
          baseTranslation,
        };
        commit('notifications/addNotification', notification, { root: true });
        throw new Error(err.response.data);
      }

      updateProductWithUnsavedKpiResults({
        product: item,
        kpiResult: response.data.scenarioResults,
        alerts: response.data.alerts,
        readFromDatabricks,
        isAgGridProductListEnabled: getters.isAgGridProductListEnabled,
      });

      if (getters.readFromDatabricks) {
        // Do not mutate item after this point
        // both Vue.set calls required to ensure hasUnsavedKpiResults reactivity
        Vue.set(item, 'hasUnsavedKpiResults', !item.revert);
        // hasPriceChanged: always true for single-item path — it is always the directly overridden product.
        Vue.set(item, 'hasPriceChanged', !item.revert);
        commit('updateItem', { updateItem: item });

        const overridesInPricingGroup = state.items.filter(
          i =>
            i.hierarchy[pricingGroupLevel].id === item.hierarchy[pricingGroupLevel].id &&
            i.hasUnsavedKpiResults
        );
        // only update parent hierarchy override state when reverting last override, or creating first override in PG
        const minOverrides = item.revert ? 0 : 1;
        if (overridesInPricingGroup.length <= minOverrides) {
          // update higher hierarchies with pending save kpi results
          commit('setHierarchyUnsavedKpiResults', {
            hierarchyKey: item.hierarchy[pricingGroupLevel].id,
            selectedCompetitors: getters.competitors,
            revert: item.revert,
          });
        }
      }

      dispatch('getAlertCounts');
      dispatch(
        'hierarchy/unsetUMApprovalIfPriceChanged',
        { levelEntryKey: item.hierarchy[unitLevel].id },
        { root: true }
      );
      // If a product is expanded and we override the price, fetch updates for the expanded view
      if (
        !isEmpty(state.expandedProduct) &&
        state.expandedProduct.scenarioResults.scenarioKey === item.scenarioKey &&
        state.expandedProduct.article.productKey === item.productKey
      ) {
        await dispatch('fetchFullProductInfo', {
          scenarioKey: state.expandedProduct.scenarioResults.scenarioKey,
          productKey: state.expandedProduct.article.productKey,
          toolStoreGroupKey: state.expandedProduct.toolStoreGroupKey,
        });
      }

      // Re-fetch price validations for the overridden product.
      // The legacy path does not return inline validation data, so we fetch
      // from the per-product API endpoint to avoid a full workpackage refetch.
      if (
        rootState.clientConfig.priceValidations &&
        rootState.clientConfig.priceValidations.enabled
      ) {
        dispatch(
          'priceValidations/fetchProductValidations',
          { workpackageId, productKey: item.productKey, scenarioKey: item.scenarioKey },
          { root: true }
        );
      }
    },

    async fetchAggregations({ state, dispatch, commit, rootState }) {
      commit('setLoading', true);
      const aggregations = [];
      const getGroupedAggregationsFlag = rootState.clientConfig.toggleLogic[getGroupedAggregations];
      const getSingleAggregationsFlag = rootState.clientConfig.toggleLogic[getSingleAggregations];
      // cancel aggregation requests
      await dispatch('cancelTimeFlexibleAggregationRequests');
      commit('resetAggregationLoadings');

      // fetch the aggregated results:
      if (getGroupedAggregationsFlag)
        aggregations.push(
          dispatch('fetchGroupedAggregatedHierarchyLevelItems', { refreshCache: true })
        );

      if (getSingleAggregationsFlag) {
        forEach(range(categoryLevel, architectureGroupLevel), aggregationLevel => {
          const parentId = state.expandedHierarchyLevelItems[aggregationLevel];
          if (parentId) {
            aggregations.push(
              dispatch('fetchAggregatedHierarchyLevelItems', { aggregationLevel, parentId })
            );
          }
        });

        // top-level aggregations:
        aggregations.push(
          dispatch('fetchAggregatedHierarchyLevelItems'),
          dispatch('fetchAggregatedHierarchyLevelItems', { aggregationLevel: unitLevel })
        );
      }

      // if store group aggregations are displayed, update them
      if (!isEmpty(state.expandedStoreGroupAggregations)) {
        aggregations.push(dispatch('fetchAllStoreGroupAggregations'));
      }
      await Promise.all(aggregations);
    },

    async toggleStoreGroupAggregations(
      { state, commit, dispatch },
      { level, parentId, scenarioKey = null }
    ) {
      const storeGroupAggregation = {
        level,
        parentId,
        scenarioKey,
      };
      const isExpanded = state.expandedStoreGroupAggregations.some(
        areStoreGroupAggregationsEqual(storeGroupAggregation)
      );
      if (isExpanded) {
        commit('removeExpandedStoreGroupAggregation', storeGroupAggregation);
        return;
      }
      commit('addExpandedStoreGroupAggregation', storeGroupAggregation);
      dispatch('fetchStoreGroupAggregations', storeGroupAggregation);
    },

    async fetchStoreGroupAggregations(
      { commit, dispatch, rootState },
      { level, parentId, scenarioKey }
    ) {
      commit('setLoadingStoreGroupAggregations', true);
      const params = getFetchStoreGroupAggregationsParams({ level, parentId, scenarioKey });
      const storeGroupOrder = rootState.clientConfig.storeGroupOrderConfig;
      const [err, results] = await to(dispatch('fetchAggregatedHierarchyLevelItems', params));
      if (err) {
        commit('setLoadingStoreGroupAggregations', false);
        commit('updateExpandedStoreGroupAggregation', {
          level,
          parentId,
          scenarioKey,
          loading: false,
          storeGroupOrder,
        });
        return Promise.reject(err);
      }
      if (!results.preventUpdate) {
        commit('updateExpandedStoreGroupAggregation', {
          level,
          parentId,
          scenarioKey,
          loading: false,
          data: results.items,
          storeGroupOrder,
        });
      }
      commit('setLoadingStoreGroupAggregations', false);
    },

    async fetchAllStoreGroupAggregations({ state, commit, dispatch, rootState }) {
      commit('setLoadingStoreGroupAggregations', true);
      const aggregations = state.expandedStoreGroupAggregations.map(agg => ({
        ...agg,
        loading: true,
        data: [],
      }));
      commit('setExpandedStoreGroupAggregations', aggregations);
      const requests = aggregations.map(agg => {
        const params = getFetchStoreGroupAggregationsParams(agg);
        return dispatch('fetchAggregatedHierarchyLevelItems', params);
      });
      const [err, results] = await to(Promise.all(requests));
      if (err) {
        commit('setLoadingStoreGroupAggregations', false);
        const newAggregations = aggregations.map(agg => ({
          ...agg,
          loading: false,
        }));
        commit('setExpandedStoreGroupAggregations', newAggregations);
        return Promise.reject(err);
      }
      const storeGroupOrder = rootState.clientConfig.storeGroupOrderConfig;
      const newAggregations = aggregations.map((agg, index) => {
        const data = results[index].preventUpdate
          ? agg.data
          : mapStoreGroupAggregationsData({
              data: results[index].items,
              products: state.items,
              storeGroupOrder,
            });
        return {
          ...agg,
          loading: false,
          data,
        };
      }, []);
      commit('setExpandedStoreGroupAggregations', newAggregations);
      commit('setLoadingStoreGroupAggregations', false);
    },

    async fetchScenariosStoreGroupAggregations({ state, dispatch }) {
      if (isEmpty(state.expandedStoreGroupAggregations)) return;
      const areScenariosStoreGroupAggregationsExpanded = state.expandedStoreGroupAggregations.some(
        agg => !!agg.scenarioKey
      );
      if (!areScenariosStoreGroupAggregationsExpanded) return;
      return dispatch('fetchAllStoreGroupAggregations');
    },

    setSelectedCompetitors({ commit, dispatch }, selectedCompetitors) {
      commit('setSelectedCompetitors', selectedCompetitors);
      // Update column state when competitors change
      dispatch('updateColumnStateForCompetitorChange');
    },

    setSelectedCompetitorAtIndex({ commit, dispatch }, { selectedCompetitor, index }) {
      commit('setSelectedCompetitorAtIndex', { selectedCompetitor, index });
      // Update column state when competitors change
      dispatch('updateColumnStateForCompetitorChange');
    },

    updatePricingGroupsToRelease({ commit }, { pg, selected = true }) {
      commit('updatePricingGroupsToRelease', { pg, selected });
    },

    resetPricingGroupsToRelease({ commit }) {
      commit('resetPricingGroupsToRelease');
    },

    setCategoryToRelease({ commit }, { category }) {
      commit('setCategoryToRelease', { category });
    },

    setPricingGroupsToRelease({ commit }, { pricingGroups }) {
      commit('setPricingGroupsToRelease', { pricingGroups });
    },

    setSelectedWeighting({ commit }, { selectedWeighting }) {
      commit('setSelectedWeighting', { selectedWeighting });
    },

    setShowRegularImpact({ commit }, { showRegularImpact }) {
      commit('setShowRegularImpact', { showRegularImpact });
    },

    setSelectedHistoricalPeriod({ commit }, { selectedHistoricalPeriod }) {
      commit('setSelectedHistoricalPeriod', { selectedHistoricalPeriod });
    },

    resetState({ commit, dispatch }) {
      dispatch('cancelTimeFlexibleAggregationRequests');
      commit('resetState');
    },

    setExpandedProduct({ commit }, product) {
      commit('setExpandedProduct', product);
    },

    async setAlertFilter({ commit, dispatch }, alertType) {
      commit('setAlertFilter', alertType);
      await Promise.all([
        dispatch('fetchAggregations'),
        dispatch('scenarioMetadata/getAggregatedScenarios', {}, { root: true }),
      ]);
    },

    async upsertActiveTableFilter({ commit, dispatch }, filter) {
      if (isArray(filter)) filter.forEach(f => commit('upsertActiveTableFilter', f));
      else commit('upsertActiveTableFilter', filter);
      await Promise.all([
        dispatch('scenarioMetadata/getAggregatedScenarios', {}, { root: true }),
        dispatch('fetchScenariosStoreGroupAggregations'),
      ]);
    },

    async removeActiveTableFilter({ commit, dispatch }, { filter, skipFetch = false }) {
      if (isArray(filter)) filter.forEach(f => commit('removeActiveTableFilter', f));
      else commit('removeActiveTableFilter', filter);
      await Promise.all([
        ...(skipFetch
          ? []
          : [dispatch('scenarioMetadata/getAggregatedScenarios', {}, { root: true })]),
        dispatch('fetchScenariosStoreGroupAggregations'),
      ]);
    },

    async getAlertCounts({ state, rootState, commit }) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const { retailAttributesFilter } = rootState.filters;
      let formattedAttributeFilters = {};

      if (retailAttributesFilter.length) {
        formattedAttributeFilters = formatFilters({
          where: retailAttributesFilter,
          rootState,
        });
      }

      const params = {
        workpackageId,
        attributeFilters: formattedAttributeFilters,
      };

      const [err, response] = await to(
        axios.get('/api/workpackage-product/alertCounts', { params })
      );

      if (err) {
        throw new Error(err.message);
      }
      commit('setAlertCounts', response.data);

      // In the event that we are filtering using the attribute filter also
      // If the alert counts is zero for the current alert filter
      // We set it to null to prevent no hierarchy levels appearing on GV
      if (get(state.alertCounts, state.alertFilter, 0) === 0) {
        commit('setAlertFilter', null);
      }
    },

    async toggleFilteringByArchitectureGroup(
      { commit, dispatch },
      {
        isFilterEnabled,
        architectureGroupAttribute,
        architectureGroupId,
        attributeFiltersToKeep = [],
      }
    ) {
      if (isFilterEnabled) {
        dispatch('filters/restoreRetailAttributesFilter', null, { root: true });
        commit('restoreGridViewFilters');
        return;
      }

      const architectureGroupFilter = {
        ...architectureGroupAttribute,
        attributeValue: [architectureGroupId],
        predicate: filterPredicates.IN,
        disabled: true,
      };
      dispatch(
        'filters/saveAndResetRetailAttributesFilter',
        [...attributeFiltersToKeep, architectureGroupFilter],
        { root: true }
      );
      commit('saveAndResetGridViewFilters');
    },

    cancelTimeFlexibleAggregationRequests({ commit, state }) {
      Object.values(state.timeFlexibleAggregationRequestsCanceller).forEach(canceller => {
        canceller.cancel();
      });
      commit('resetTimeFlexibleAggregationRequestsCanceller');
      commit('resetTimeFlexibleAggregationsLoading');
    },

    async saveKpiResultsToDatabricks(
      { commit, dispatch, rootState },
      { scenarioKeys, levelEntryKey, isUnitManagerView }
    ) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      commit('setSavingKpiResults', true);

      const [err, response] = await to(
        axios.post(
          `/api/scenario-results/workpackage/${workpackageId}/save-kpi-results-to-databricks`,
          { scenarioKeys }
        )
      );
      if (err) {
        commit('setSavingKpiResults', false);
        const jobId = 'save_kpi_results_to_databricks';
        const baseTranslation = `notifications.${jobId}`;
        const jobStatus = 'failed';
        const notification = {
          // ensure id is unique so message is always displayed.
          id: `${jobId}:${jobStatus}:${Date.now()}`,
          jobStatus,
          notificationPayload: workpackageId,
          baseTranslation,
        };
        commit('notifications/addNotification', notification, { root: true });
        throw new Error(err.response.data);
      }

      await to(
        Promise.all([
          dispatch('fetchItems', { levelEntryKey, isUnitManagerView }),
          dispatch('fetchAggregations'),
          dispatch('scenarioMetadata/getAggregatedScenarios', {}, { root: true }),
          dispatch('getAlertCounts'),
        ])
      );

      commit('setSavingKpiResults', false);

      return response.data;
    },

    setVisibleCompetitors({ commit }, visibleCompetitors) {
      commit('setVisibleCompetitors', visibleCompetitors);
    },

    setCurrentColumnState({ commit }, columnState) {
      commit('setCurrentColumnState', columnState);
    },

    /**
     * Updates the column state to reflect changes in Top level competitor selection.
     * This method iterates through all columns and updates the visibility of competitor columns
     * based on the current selectedCompetitors and visibleCompetitors state.
     */
    async updateColumnStateForCompetitorChange({ state, commit, rootState }) {
      if (!state.currentColumnState) {
        return;
      }

      const activeLayout = rootState.gridLayouts.activeLayout[gridTypes.GRIDVIEW];
      const layoutHideByColId = buildLayoutHideLookup(activeLayout);

      // Get the current column state from the grid
      const currentColumnState = state.currentColumnState;

      // Create a new column state that reflects the current selectedCompetitors
      const updatedColumnState = currentColumnState.map(column => {
        // Check if this is a competitor column by examining both colId and field
        const columnIdentifier = column.colId || column.field;

        if (columnIdentifier && columnIdentifier.includes('competitors.')) {
          // Check if this competitor column belongs to a tool store group that's completely hidden
          const isInHiddenToolStoreGroup = agGridUtils.isColumnInHiddenToolStoreGroup(
            column,
            currentColumnState
          );

          // If the parent tool store group is hidden, don't change this column's visibility
          if (isInHiddenToolStoreGroup) {
            return column; // Return unchanged column
          }

          return {
            ...column,
            hide: getCompetitorColumnHide(
              columnIdentifier,
              state.selectedCompetitors,
              state.visibleCompetitors,
              layoutHideByColId
            ),
          };
        }

        // Return unchanged column for non-competitor columns
        return column;
      });

      commit('setCurrentColumnState', updatedColumnState);
    },

    /**
     * Updates column state based on tool store group filter and layout preferences.
     *
     * PRIORITY ORDER for TSG columns:
     * 1. If TSG is explicitly mentioned by filter: always show it, regardless of layout config.
     * 2. If TSG is filtered out: always hide it.
     * 3. Otherwise (no filter): respect the layout's column visibility.
     *
     * For competitor columns within allowed TSGs:
     * - Delegate to getCompetitorColumnHide (layout wins when it has an explicit entry,
     *   otherwise falls back to selectedCompetitors/visibleCompetitors visibility).
     *
     * @param {Object} [options]
     * @param {Array} [options.columnState] - Column state to use instead of state.currentColumnState.
     * @returns {Array|undefined} Updated column state array, or undefined if no column state is available.
     */
    async updateColumnStateForToolStoreGroupFilter(
      { state, commit, rootState },
      { columnState: providedColumnState } = {}
    ) {
      // Only update if we have an current column state
      if (!state.currentColumnState && !providedColumnState) {
        return;
      }

      // Get the current column state - use provided columnState if available, otherwise from store
      const currentColumnState = providedColumnState || state.currentColumnState;

      // Get the current tool store group filter from the filters store
      const { retailAttributesFilter } = rootState.filters;

      // Read active layout and build necessary data structures
      const activeLayout = rootState.gridLayouts.activeLayout[gridTypes.GRIDVIEW];

      // Get filter values (needed for column processing logic)
      const toolStoreGroupValue = getFilterValue(
        retailAttributesFilter,
        filterOptions.storegroupOption
      );
      const chainValue = getFilterValue(retailAttributesFilter, filterOptions.chainOption);

      // Get allowed tool store group keys and layout information
      const allowedToolStoreGroupKeys = getAllowedToolStoreGroupKeys(
        retailAttributesFilter,
        activeLayout,
        rootState.storeGroupRelationships
      );
      const layoutHideByColId = buildLayoutHideLookup(activeLayout);
      const layoutVisibleToolStoreGroupKeys = activeLayout
        ? agGridUtils.extractVisibleToolStoreGroupKeys(activeLayout.columnState)
        : [];

      // Create a new column state that reflects the current tool store group filter
      const updatedColumnState = currentColumnState.map(column =>
        processColumnForToolStoreGroupFilter(
          column,
          allowedToolStoreGroupKeys,
          layoutHideByColId,
          layoutVisibleToolStoreGroupKeys,
          toolStoreGroupValue,
          chainValue,
          state
        )
      );

      commit('setCurrentColumnState', updatedColumnState);

      return updatedColumnState;
    },

    async handleCrossElasticityOptimisation(
      { dispatch },
      { scenarioKey, workpackageId, toolStoreGroupKeys = null } = {}
    ) {
      await dispatch('runCrossElasticityOptimisation', {
        scenarioKey,
        workpackageId,
        toolStoreGroupKeys,
      });
    },

    async runCrossElasticityOptimisation(
      { commit },
      { scenarioKey, workpackageId, toolStoreGroupKeys = null }
    ) {
      commit('setCalculatingScenarioElasticityResults', true);
      if (!isEmpty(toolStoreGroupKeys)) {
        commit('setCalculatingTsgElasticityKeys', toolStoreGroupKeys);
      }

      try {
        await axios.post(
          `/api/cross-elasticity-optimisation/${scenarioKey}/workpackage/${workpackageId}/optimise-prices`,
          { toolStoreGroupKeys }
        );
      } catch (err) {
        commit('setCalculatingScenarioElasticityResults', false);
        commit('setCalculatingTsgElasticityKeys', []);
        console.info(err);
      }
    },

    async checkCrossElasticityStatus({ commit, rootState }, scenarioKey = null) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      scenarioKey = scenarioKey || rootState.filters.scenario;
      if (workpackageId && scenarioKey) {
        const [err, res] = await to(
          axios.get(
            `/api/cross-elasticity-optimisation/${scenarioKey}/workpackage/${workpackageId}/optimise-prices/status`
          )
        );
        if (err) {
          commit('setCalculatingScenarioElasticityResults', false);
          commit('setCalculatingTsgElasticityKeys', []);
          throw new Error(err.message);
        }
        const { data: optimisationIsRunning } = res;
        commit('setCalculatingScenarioElasticityResults', optimisationIsRunning);
        if (!optimisationIsRunning) {
          // Clear per-TSG keys — optimisation is no longer running so no TSG can be active.
          // Guards against stale keys if the completion handler was missed (e.g. WebSocket disconnect).
          commit('setCalculatingTsgElasticityKeys', []);
        }
      }
    },
  },
};

export default store;
